/**
 * The Carn domain model.
 *
 * Authorised by: product-concept §5 (DECIDED: "this is the foundational data and
 * interaction model"), §6 (Capsules and tiering), §8 (surfacing conditions), §9.2
 * (energy states), implementation-brief §9 ("no overdue state in any schema").
 *
 * STRUCTURAL LAWS ENFORCED BY THIS FILE — read before editing:
 *
 *   1. There is no `overdue` state, field, or derivation. Loops cool; they never turn
 *      red. This is not a UI choice; the type system must make overdue unspeakable
 *      (product-concept §5). A `Temperature` union with five members is the whole
 *      vocabulary and it does not include one.
 *   2. Temperature is DERIVED, never stored as user intent. The user never files
 *      anything into a temperature (§5, objection 1). `deriveTemperatures` is the only
 *      function that can produce a `DerivedTemperature`, and every consumer that must
 *      know a loop's state takes that branded type — so "file this as warm" is a
 *      compile error, not a code-review conversation.
 *   3. History is append-only and nothing is ever taken away by time
 *      (design-direction §6, objection 1; implementation-brief §2). The cairn is the
 *      `stones` log, not an integer: `sessionCount()` and `investedMs()` are folds over
 *      it, so `sessionCount = 0` is not a statement anyone can write.
 *   4. `released` is a terminal SUCCESS state, equal in standing to `closed`
 *      (product-concept §13: "Releases count as success, equal to completions").
 */

import { RECOVERY_JOURNAL_DAYS } from './journal';
import type { SectionId } from './lists';
import type { ReleaseObservation, TimeOfDayBand } from './observation';
// `RULINGS R162`'s formatter needs the band table and has an hour rather than an instant. One
// definition of where the evening starts, in the module that names the vocabulary.
import { bandForHour } from './observation';

// ---------------------------------------------------------------------------
// Temperature
// ---------------------------------------------------------------------------

/**
 * The complete state vocabulary of a Loop.
 *
 * `warm`       — active/recent, on the resumption surface
 * `cooling`    — present but quiet. Either genuinely untouched for a while, or
 *                warm-adjacent-but-over-the-cap (product-concept §5). The two are NOT
 *                the same thing to a user, which is why every verdict carries a
 *                `reason` — see `TemperatureReason`.
 * `composting` — long-idle or explicitly settled, visibly settling toward release
 * `closed`     — done (including "good enough")
 * `released`   — deliberately let go. A positive terminal state.
 *
 * Exactly five members, forever (CLAUDE.md). `@carn/tokens` imports this union and
 * `TEMPERATURES` rather than redeclaring them, so the two vocabularies cannot drift.
 */
export const TEMPERATURES = ['warm', 'cooling', 'composting', 'closed', 'released'] as const;
export type Temperature = (typeof TEMPERATURES)[number];

/**
 * LAW 2, made structural (critique 1.4). A `DerivedTemperature` is a `Temperature` that
 * provably came out of `deriveTemperatures`: the brand is a private unique symbol, so no
 * other module can mint one, while `DerivedTemperature` remains assignable to
 * `Temperature` for rendering (`@carn/tokens`, `@carn/ui`).
 */
declare const DERIVED_TEMPERATURE: unique symbol;
export type DerivedTemperature = Temperature & { readonly [DERIVED_TEMPERATURE]: true };

/**
 * WHY a loop is where it is. product-concept §5 has a name for the loops that are
 * recent but over the cap — "warm-adjacent but not shouting" — and the five-member
 * vocabulary deliberately cannot express it (a sixth Temperature is forbidden).
 *
 * The copy layer MUST branch on `reason` to pick honest strings: saying "3 cooling —
 * look when you are ready" about loops touched minutes ago (`reason: 'over-cap'`) is a
 * false statement, and this audience notices (design-direction §1, objection 2).
 *
 * `active`   — the loop the user is on right now. Unconditionally warm.
 * `deadline` — a hard external commitment is close. Warm above the cap if necessary,
 *              because design-direction §3 objection 3 traded the red badge for
 *              prominence *including persistence*.
 * `caught`   — captured and never once picked up. Warm, and NOT a card on the
 *              resumption surface (docs/DEVIATIONS.md D44, amending D38).
 * `recent`   — among the most-recently-touched, inside the cap.
 * `over-cap` — recently touched, but the warm set was full. Warm-adjacent.
 * `window`   — genuinely untouched for a while; the recency window decided this.
 * `settled`  — the user explicitly tucked this away (bulk compost, §11).
 * `terminal` — closed or released. Not derived from time at all.
 */
export const TEMPERATURE_REASONS = [
  'active',
  'deadline',
  'caught',
  'recent',
  'over-cap',
  'window',
  'settled',
  'terminal',
] as const;
export type TemperatureReason = (typeof TEMPERATURE_REASONS)[number];

export type TemperatureVerdict = {
  readonly temperature: DerivedTemperature;
  readonly reason: TemperatureReason;
};

export const TERMINAL_TEMPERATURES = ['closed', 'released'] as const;
export type TerminalTemperature = (typeof TERMINAL_TEMPERATURES)[number];

export function isTerminal(t: Temperature): t is TerminalTemperature {
  return t === 'closed' || t === 'released';
}

/** The vertical axis is the temperature metaphor made spatial (design-direction §4). */
export const TEMPERATURE_DEPTH: Record<Temperature, number> = {
  warm: 0,
  cooling: 1,
  composting: 2,
  closed: 3,
  released: 3,
};

/**
 * Colour is always redundant with a second channel (design-direction §3).
 * These are the non-colour channels, declared next to the state itself so a component
 * cannot render a temperature using colour alone.
 */
/**
 * `as const` is load-bearing, not stylistic. `packages/ui` binds this table to its icon
 * set with `satisfies Record<Temperature, WaymarkName>`, which is what makes "a
 * temperature with no shape" a BUILD ERROR rather than a review finding. Widening the
 * values to `string` silently disarms that guard.
 */
export const TEMPERATURE_GLYPH = {
  warm: 'waymarker-lit',
  cooling: 'waymarker',
  composting: 'ground-layers',
  closed: 'cairn-complete',
  released: 'open-hand',
} as const satisfies Record<Temperature, string>;

// ---------------------------------------------------------------------------
// Time windows
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

export type TemperatureWindows = {
  /**
   * Beyond the warm set, touched inside this window -> `cooling` with reason
   * `over-cap` rather than `window`: it is warm-adjacent, not cold.
   */
  readonly warmDays: number;
  /** Beyond warm, inside this -> cooling. Beyond it -> composting. */
  readonly coolingDays: number;
};

export const DEFAULT_WINDOWS: TemperatureWindows = { warmDays: 4, coolingDays: 21 };

/**
 * The warm cap (product-concept §5). Soft by construction: this module exposes it as a
 * count for ranking and a prompt, and there is deliberately no function anywhere that
 * refuses a promotion. The active loop and imminent hard deadlines are admitted above
 * it (see `deriveTemperatures`).
 *
 * `MIN_WARM_CAP` stays 1 — see docs/DEVIATIONS.md D4. A user who says "put one thing in
 * front of me" is obeyed; the honesty problem the critique raised is fixed by
 * `TemperatureReason`, not by overriding the user.
 */
export const DEFAULT_WARM_CAP = 5;
export const MIN_WARM_CAP = 1;
export const MAX_WARM_CAP = 12;

/**
 * Prominence at or above this admits a loop to the warm set regardless of recency.
 * 0.55 is `deadlineProminence`'s "three days or fewer" step.
 */
export const DEADLINE_WARM_THRESHOLD = 0.55;

// ---------------------------------------------------------------------------
// Origin — how and where a loop was caught
// ---------------------------------------------------------------------------

/**
 * Origin is preserved because "origin is often the strongest memory hook for ND
 * recall" (product-concept §5). It is never editable and never lost.
 */
export type CaptureChannel =
  | 'voice'
  | 'photo'
  | 'text'
  | 'share-sheet'
  | 'interrupt'
  | 'walkaround'
  | 'tossed-to-me'
  | 'ask-reply';

export type Origin = {
  readonly channel: CaptureChannel;
  readonly capturedAt: number;
  /** Coarse place label only. Raw coordinates never leave the device (brief §3). */
  readonly placeLabel?: string;
  /** Display name of the person who tossed it, for `tossed-to-me`. */
  readonly fromPerson?: string;
};

// ---------------------------------------------------------------------------
// Capsules
// ---------------------------------------------------------------------------

/**
 * Capsule tiering (product-concept §6.1). The design "assumes the user gives us
 * nothing and degrades gracefully upward" — so Tier 0 fields are non-optional and
 * every richer field is optional.
 *
 * Tier 0 — fully passive, always happens: timestamp, place, last step, time invested.
 * Tier 1 — one gesture: the Interrupt Button's `interruptedBy`.
 * Tier 2 — five seconds, solicited only at calm: voice line or photo.
 */
export type CapsuleTier = 0 | 1 | 2;

export type Capsule = {
  readonly id: string;
  readonly loopId: string;
  /** Tier 0. */
  readonly createdAt: number;
  /** Tier 0. Milliseconds of recorded work before this pause. */
  readonly investedMs: number;
  /** Tier 0. Coarse label, derived on-device. */
  readonly placeLabel?: string;
  /** Tier 0. Whatever the app last observed the user doing on this loop. */
  readonly lastStep?: string;
  /**
   * The first physical action (product-concept §9.1): bodily, concrete, sub-two-minute.
   * "The first physical action is what the capsule stores as `next`."
   */
  readonly nextAction?: string;
  /** Tier 1 — the Interrupt Button's single tap. */
  readonly interruptedBy?: string;
  /** Tier 2. Size-capped — see MAX_VOICE_NOTE_* / MAX_PHOTO_BYTES (brief §8). */
  readonly voiceNoteUri?: string;
  readonly voiceNoteDurationMs?: number;
  readonly voiceNoteBytes?: number;
  readonly voiceNoteTranscript?: string;
  readonly photoUri?: string;
  readonly photoBytes?: number;
};

/**
 * The two Tier-2 attachments, named as a closed set (product-concept §6.1: "a photo… a ≤15-second
 * voice note").
 *
 * IT EXISTS BECAUSE THEY CAN BE TAKEN BACK OFF (`removeCapsuleMedia` in ./events.ts). Every other
 * caller reaches for `photoUri` or `voiceNoteUri` by name; a caller that removes one needs to SAY
 * WHICH, and a `'photo' | 'voice-note'` union keeps that a compile-time question rather than a
 * string a screen invents. A third member would mean §6.1 gained a third attachment, which is a
 * product decision and not an implementation one.
 *
 * `'voice-note'` NAMES THE WHOLE ATTACHMENT — the recording, its duration, its size and its
 * transcript. §7.3's "silent equivalent" makes the transcript the same object seen the other way
 * round, not a separate thing that could survive its own audio.
 */
export type CapsuleMedium = 'photo' | 'voice-note';

/** Does this capsule carry that attachment at all? The question `removeCapsuleMedia` no-ops on. */
export function capsuleCarries(capsule: Capsule, medium: CapsuleMedium): boolean {
  return medium === 'photo'
    ? capsule.photoUri !== undefined || capsule.photoBytes !== undefined
    : capsule.voiceNoteUri !== undefined ||
        capsule.voiceNoteDurationMs !== undefined ||
        capsule.voiceNoteBytes !== undefined ||
        capsule.voiceNoteTranscript !== undefined;
}

export function capsuleTier(capsule: Capsule): CapsuleTier {
  if (capsule.voiceNoteUri || capsule.photoUri) return 2;
  if (capsule.interruptedBy) return 1;
  return 0;
}

/**
 * Offline media caps (implementation-brief §8: "size-capped offline media"), plus
 * product-concept §6.1's "≤15-second voice note".
 *
 * These are refusals to attach, never refusals to park: a capsule whose media breaches
 * a cap is still a Tier-0 capsule, so the §6.1 guarantee is untouched.
 */
export const MAX_VOICE_NOTE_MS = 15_000;
export const MAX_VOICE_NOTE_BYTES = 512 * 1024;
export const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

export type MediaBreach = 'voice-too-long' | 'voice-too-large' | 'photo-too-large';

/**
 * Returns the breach, or `undefined` when the media fits. Callers must show the user a
 * copy-catalogue string; core deliberately holds no user-visible English.
 */
export function capsuleMediaBreach(args: {
  readonly voiceNoteDurationMs?: number;
  readonly voiceNoteBytes?: number;
  readonly photoBytes?: number;
}): MediaBreach | undefined {
  if (args.voiceNoteDurationMs !== undefined && args.voiceNoteDurationMs > MAX_VOICE_NOTE_MS) {
    return 'voice-too-long';
  }
  if (args.voiceNoteBytes !== undefined && args.voiceNoteBytes > MAX_VOICE_NOTE_BYTES) {
    return 'voice-too-large';
  }
  if (args.photoBytes !== undefined && args.photoBytes > MAX_PHOTO_BYTES) {
    return 'photo-too-large';
  }
  return undefined;
}

/**
 * A Tier-0 capsule is guaranteed: even a loop abandoned in total chaos has one
 * (product-concept §6.1). This is the only capsule constructor, so the guarantee
 * cannot be bypassed.
 */
export function makeTier0Capsule(args: {
  id: string;
  loopId: string;
  now: number;
  investedMs: number;
  placeLabel?: string;
  lastStep?: string;
  nextAction?: string;
}): Capsule {
  return {
    id: args.id,
    loopId: args.loopId,
    createdAt: args.now,
    investedMs: Math.max(0, args.investedMs),
    placeLabel: args.placeLabel,
    lastStep: args.lastStep,
    nextAction: args.nextAction,
  };
}

/** Capsule imagery is optional per user (product-concept §6, objection 3). */
export type CapsuleImagery = { readonly capsulePhotos: boolean };

/**
 * Text-only rendering preference (product-concept §6, objection 3: "any Loop's capsule
 * can be text-only per user preference" — photos of half-done jobs must not become a
 * museum of mess). This hides a field from a render; it never deletes one.
 */
export function visibleCapsuleFields(capsule: Capsule, prefs: CapsuleImagery): Capsule {
  if (prefs.capsulePhotos) return capsule;
  return { ...capsule, photoUri: undefined, photoBytes: undefined };
}

// ---------------------------------------------------------------------------
// Dates — hard external deadline vs soft intention
// ---------------------------------------------------------------------------

/**
 * The single toggle made at capture (product-concept §5): "Does the world care when
 * this happens, or just you?"
 *
 * `hard` — a real external commitment (vet appointment). Gets calendar-grade
 *          treatment and escalating, user-configured reminders.
 * `soft` — an intention. Allowed to slide with no visual punishment, ever.
 */
export type DateKind = 'hard' | 'soft';

export type LoopDate = {
  readonly kind: DateKind;
  /** Epoch ms. */
  readonly at: number;
  /** Only meaningful for `hard`. Reminder offsets in ms before `at`. */
  readonly reminderOffsetsMs?: readonly number[];
};

const HOUR_MS = 60 * 60 * 1000;

/**
 * The default reminder ladder for a HARD date (product-concept §5: hard deadlines get
 * "calendar-grade treatment and escalating, user-configured reminders"; RULINGS R33).
 *
 * Four escalating offsets, expressed as milliseconds BEFORE `LoopDate.at` — the sign
 * convention `LoopDate.reminderOffsetsMs` already documents: a week out, a day out, two
 * hours out, and the hour itself.
 *
 * CORE OWNS THE OFFSETS; IT DOES NOT OWN THE SCHEDULER. Scheduling is `apps/mobile`'s
 * (RULINGS R7/R33), through the protected `deadline` notification class that the quieting
 * gesture already cannot silence. This file has no I/O and never will.
 */
export const DEFAULT_REMINDER_OFFSETS_MS: readonly number[] = [
  7 * DAY_MS,
  DAY_MS,
  2 * HOUR_MS,
  0,
];

/**
 * The ladder as it applies to a date being set NOW: truncated to the offsets still in the
 * future (RULINGS R33).
 *
 * A vet appointment entered the evening before must not schedule a reminder for last
 * Tuesday. Truncation rather than clamping-to-now is deliberate: three notifications
 * arriving at once for one appointment is the frequency ceiling product-concept §11 exists
 * to prevent, and an offset that has already passed carries no information the remaining
 * ones do not.
 *
 * THE COMPARISON IS `>=`, not `>`. "Still in the future" has to include the boundary: a date
 * entered exactly a week out — "next Tuesday", typed on a Tuesday — otherwise loses the
 * week-out rung to a strict inequality, which is the one rung a week-out date most needs (the
 * critique's S3.5).
 */
export function defaultReminderOffsetsMs(dateAt: number, now: number): readonly number[] {
  return DEFAULT_REMINDER_OFFSETS_MS.filter((offset) => dateAt - offset >= now);
}

/**
 * Urgency through importance, not through threat (design-direction §3, objection 3):
 * a hard deadline earns PROMINENCE (size, position, persistence) and never an alarm
 * colour or an overdue state.
 *
 * Returns 0..1. A hard date in the past keeps prominence 1 — the thing still matters —
 * but the caller must render that as importance, never as failure.
 */
/**
 * How long a passed hard date keeps its prominence before letting go.
 *
 * A commitment that has just passed is still the most actionable thing on the surface —
 * the appointment was this morning, you may still need to ring them. A commitment that
 * passed four weeks ago is not, and pinning it there is how "no overdue" gets rebuilt out
 * of size and position instead of colour.
 */
export const DEADLINE_GRACE_DAYS = 2;

/**
 * Urgency through importance, not through threat (design-direction §3, objection 3):
 * a hard deadline earns PROMINENCE (size, position, persistence) and never an alarm
 * colour or an overdue state.
 *
 * Returns 0..1, rising as the date approaches, then DECAYING once it has passed.
 *
 * The decay is the whole point, and it was missing. The first version returned `1` for any
 * past date, for ever. An adversarial review found the consequence in a screenshot: the
 * lapsed-user welcome — the surface implementation-brief §11 requires to be "warm and
 * wall-free" — led with a vet appointment from twenty-eight days earlier, pinned above the
 * warm cap, in prominent type, with nothing saying it had passed. That is an overdue state.
 * It had no red in it, and it was still an overdue state, which is exactly the failure LAW 1
 * exists to prevent: the law is about what the user is made to feel, not about a hex value.
 *
 * After the grace window a passed commitment is an ordinary loop again. It is not hidden,
 * not marked, and not punished — it simply stops shouting, and recency governs it like
 * everything else.
 */
export function deadlineProminence(date: LoopDate | undefined, now: number): number {
  if (!date || date.kind !== 'hard') return 0;
  const msUntil = date.at - now;

  if (msUntil <= 0) {
    const daysSince = -msUntil / DAY_MS;
    if (daysSince >= DEADLINE_GRACE_DAYS) return 0;
    // Linear release across the grace window, from the pre-date peak down to nothing.
    return 0.95 * (1 - daysSince / DEADLINE_GRACE_DAYS);
  }

  const daysUntil = msUntil / DAY_MS;
  if (daysUntil >= 14) return 0.1;
  if (daysUntil >= 7) return 0.3;
  if (daysUntil >= 3) return 0.55;
  if (daysUntil >= 1) return 0.8;
  return 0.95;
}

// ---------------------------------------------------------------------------
// Surfacing conditions — lists come to you (product-concept §8.1)
// ---------------------------------------------------------------------------

export type EnergyLevel = 'foggy' | 'steady' | 'charged';

export const SURFACING_AXES = ['place', 'rhythm', 'energy'] as const;
export type SurfacingAxis = (typeof SURFACING_AXES)[number];

export type SurfacingCondition =
  /** Geofence. Evaluated ON-DEVICE ONLY — privacy invariant, brief §3, objection 2. */
  | { readonly axis: 'place'; readonly placeId: string; readonly placeLabel: string }
  /** Time/rhythm, e.g. Sunday mornings. `weekday` 0=Sunday. */
  | {
      readonly axis: 'rhythm';
      readonly weekday?: number;
      readonly hourFrom?: number;
      readonly hourTo?: number;
    }
  /** Energy fit. Foggy days show tiny physical loops; charged days the meaty ones. */
  | { readonly axis: 'energy'; readonly fits: readonly EnergyLevel[] };

export type SurfacingContext = {
  readonly now: number;
  /** Coarse place IDs the device currently believes it is inside. Never sent anywhere. */
  readonly presentPlaceIds?: readonly string[];
  /** Undefined when unset or decayed — no feature may depend on it existing (§9.2). */
  readonly energy?: EnergyLevel;
};

/**
 * Three-valued on purpose.
 *
 * `false` IS suppression, and a boolean has nowhere to put "the axis cannot be judged".
 * product-concept §9.2 makes the energy self-report "never mandatory, decays gracefully if
 * unset", and its objection 2 resolves on "no feature depends on it existing" — so an unset
 * energy must not be allowed to decide anything. It used to return `false`, and because
 * `matchIn` needs at least one condition to fire, a loop whose only trigger was
 * `{ axis: 'energy', fits: ['foggy'] }` — the exact shape §9.2 describes — was a candidate
 * when energy was set and NEVER a candidate when it was not. The comment on that line claimed
 * graceful decay while the line broke it (critique 9.1).
 *
 * `unknown` is what an unjudgeable axis returns; `matchIn` treats it as permissive only when
 * nothing else on the loop fired, so a definite axis still decides when there is one.
 */
export type ConditionVerdict = 'match' | 'no-match' | 'unknown';

export function evaluateCondition(
  condition: SurfacingCondition,
  ctx: SurfacingContext,
): ConditionVerdict {
  switch (condition.axis) {
    case 'place':
      return (ctx.presentPlaceIds ?? []).includes(condition.placeId) ? 'match' : 'no-match';
    case 'rhythm': {
      const d = new Date(ctx.now);
      if (condition.weekday !== undefined && d.getDay() !== condition.weekday) return 'no-match';
      const hour = d.getHours();
      if (condition.hourFrom !== undefined && hour < condition.hourFrom) return 'no-match';
      if (condition.hourTo !== undefined && hour >= condition.hourTo) return 'no-match';
      return 'match';
    }
    case 'energy':
      // No self-report today is not a "no" (product-concept §9.2).
      if (ctx.energy === undefined) return 'unknown';
      return condition.fits.includes(ctx.energy) ? 'match' : 'no-match';
  }
}

/**
 * The stretch of local time a rhythm condition is currently inside — its *window*.
 *
 * `from` inclusive, `to` exclusive, matching `evaluateCondition`'s `hour >= hourFrom` /
 * `hour < hourTo` above.
 */
export type RhythmWindow = { readonly from: number; readonly to: number };

/**
 * The window a rhythm condition is in RIGHT NOW, or `undefined` when it is not in one
 * (product-concept §8.1; the same-window suppression in ./surfacing.ts).
 *
 * IT LIVES HERE, THREE LINES UNDER `evaluateCondition`, ON PURPOSE. The window is not a new
 * concept — it is the set of instants that branch already answers `'match'` for, named. Two
 * functions in two files that must agree about "Sunday morning" is the drift this repository
 * keeps catching (see `constants.ts` in @carn/schema for the same argument about enums), and
 * the agreement is enforced structurally: this returns `undefined` unless the condition
 * matches `now`, by asking `evaluateCondition` rather than by re-deriving the answer. So the
 * window can never be non-empty at an instant the evaluator calls `no-match`.
 *
 * BOUNDARIES ARE BUILT WITH `Date`'s LOCAL CONSTRUCTOR, NOT BY ADDING MILLISECONDS. This is
 * the DST edge and it is cheap to get right: `startOfLocalDay + 9 * HOUR` is not 09:00 on the
 * two days a year a local day is 23 or 25 hours long, whereas `new Date(y, m, d, 9)` is 09:00
 * by definition. `to` uses `hourTo ?? 24`, which the constructor normalises to local midnight
 * at the start of the next day — again the right answer on a short or long day.
 *
 * TWO EDGES THAT REMAIN, STATED RATHER THAN HIDDEN:
 *
 *   - A window hour that does not exist locally (01:00–02:00 in a zone that springs forward
 *     at 01:00) normalises FORWARD to the next real instant, so the window is up to an hour
 *     shorter on that one day. An ambiguous repeated hour resolves to its first occurrence,
 *     so the window opens the earlier of the two. Both are `Date`'s own rules and neither is
 *     silently wrong in a direction that matters: the failure mode is that a chore tended in
 *     the shifted hour may surface once more that day, or be suppressed an hour early. It
 *     never brings anything into existence, and it never removes a stone.
 *   - The device's zone at `now` is the only zone there is. A tend recorded in Cardiff and a
 *     window computed in Auckland are two different local days, and this function has no way
 *     to know that — @carn/core has "no I/O, no platform imports" (CLAUDE.md) and therefore
 *     no zone history. A user who flies mid-window may see one extra surfacing.
 *
 * A window that wraps midnight (`hourFrom: 22, hourTo: 2`) is unreachable here, because
 * `evaluateCondition` never matches one — `hour >= 22 && hour < 2` is empty — so there is
 * nothing to suppress and nothing to represent. That is a pre-existing property of the
 * vocabulary, not a decision taken here; extending the rhythm vocabulary is deferred
 * (docs/OPEN-QUESTIONS.md, "Recurring work", item 2).
 */
export function currentRhythmWindow(
  condition: SurfacingCondition,
  now: number,
): RhythmWindow | undefined {
  if (condition.axis !== 'rhythm') return undefined;
  if (evaluateCondition(condition, { now }) !== 'match') return undefined;

  const d = new Date(now);
  const year = d.getFullYear();
  const month = d.getMonth();
  const day = d.getDate();

  return {
    from: new Date(year, month, day, condition.hourFrom ?? 0).getTime(),
    to: new Date(year, month, day, condition.hourTo ?? 24).getTime(),
  };
}

/**
 * The window BEFORE the one `now` is inside — yesterday's morning, or last Sunday's.
 *
 * IT LIVES HERE FOR THE REASON `currentRhythmWindow` DOES, and the reason is now doubled: three
 * functions that must agree about "Sunday morning" is worse than two. `undefined` unless the
 * condition is currently inside a window, so the pair can never disagree about which window is
 * "current", and the step back is the only new fact: one day for a daily condition, seven for one
 * pinned to a weekday. That is the whole recurrence vocabulary the design has (`RULINGS R124`
 * item 2 defers intervals, deliberately, because an interval needs an anchor date and an anchor
 * date is the doorway overdue comes through).
 *
 * BOUNDARIES ARE BUILT WITH `Date`'s LOCAL CONSTRUCTOR — same rule, same reason. `day - step`
 * underflowing a month or a year is `Date`'s own normalisation (day 0 is the last day of the
 * previous month), and on the two days a year a local day is 23 or 25 hours long, `new Date(y, m,
 * d - 1, 9)` is still 09:00 yesterday whereas `windowFrom - 24 * HOUR` is 08:00 or 10:00.
 *
 * IT IS NOT EXPORTED, AND THAT IS A STRUCTURAL DECISION RATHER THAN AN OVERSIGHT (CLAUDE.md:
 * "There is no streak anywhere"; design-direction §6 objection 1; `RULINGS R124` item 6).
 * `tendSatisfiesWindow` below is its only consumer and needs one number from it — the previous
 * window's `to` — so nothing outside this file has a reason to hold it. When it WAS exported, the
 * streak `lastTendedAt`'s docblock forbids was eight lines of caller code using only exported
 * functions: seed an anchor at `now`, take `currentRhythmWindow`, step the anchor back to the
 * midpoint of `previousRhythmWindow`, repeat fourteen times, and count which windows hold a tend.
 * That produces "6 of the last 14" — a number that can go down, computed out of a model whose
 * whole legality rests on a missed window not existing. The prohibition was written as a comment
 * and the primitive that completed it was public, which is `RULINGS R126` Part 2's lesson in the
 * other direction: an invariant nothing enforces.
 *
 * HONEST LIMIT, because a false guarantee is worse than a stated weakness. This makes the walk
 * unavailable, not the arithmetic impossible. `evaluateCondition` is public and must be — it is how
 * any caller judges a condition at an instant — so a determined caller can still probe it hour by
 * hour and rebuild the window sequence itself. The difference is between a primitive that hands
 * you the sequence and a search you have to write on purpose, which is a real difference in grade
 * and not a claim of immunity. See the report accompanying this change.
 */
function previousRhythmWindow(
  condition: SurfacingCondition,
  now: number,
): RhythmWindow | undefined {
  if (currentRhythmWindow(condition, now) === undefined) return undefined;
  if (condition.axis !== 'rhythm') return undefined;

  const d = new Date(now);
  const year = d.getFullYear();
  const month = d.getMonth();
  // The vocabulary is weekday-plus-window and nothing else, so the previous occurrence is
  // exactly one day back, or one week back when the condition names a weekday.
  const day = d.getDate() - (condition.weekday === undefined ? 1 : 7);

  return {
    from: new Date(year, month, day, condition.hourFrom ?? 0).getTime(),
    to: new Date(year, month, day, condition.hourTo ?? 24).getTime(),
  };
}

/**
 * Does a tend at `tended` mean this condition's CURRENT window has been done?
 *
 * WHICH OCCURRENCE A TEND BELONGS TO IS THE WHOLE QUESTION, and "inside the window" was the
 * wrong answer to it. A chore done at 06:50 for a 07:00–09:00 window was not counted, so the
 * user fed the chickens and the app asked for them forty minutes later — the notification spam
 * design-direction §8 objection 1 resolves against, and the app contradicting something it has
 * been told. Morning chores are routinely done before the nominal hour.
 *
 * SO A TEND IN THE GAP IS ATTRIBUTED TO THE NEARER OCCURRENCE, and the split is the gap's
 * midpoint. The two rejected alternatives are worth naming, because each is wrong in one
 * direction and this rule is the only one that is wrong in neither:
 *
 *   - "inside the window" (what shipped) mis-attributes an EARLY tend to the previous
 *     occurrence, so the chore comes back the moment it is done.
 *   - "any time since the previous window closed" mis-attributes a LATE tend to the next
 *     occurrence: a daily 07:00–09:00 chore done at 09:30 today would be suppressed tomorrow
 *     morning, so the app silently skips a day the user was relying on. Symmetrical, and no
 *     better.
 *
 * The midpoint says what a human means: 06:50 belongs to the seven o'clock feed, 09:30 belongs
 * to the one just finished, Saturday evening belongs to Sunday's weekly job, Monday morning does
 * not. When windows are contiguous (an all-day daily condition) the gap is empty and this
 * degenerates exactly to "inside the window", which is why nothing about the simple case moved.
 *
 * IT INVENTS NO STATE. Two locally-constructed boundaries and a comparison — no stored "next
 * due", no counter, and nothing that could be late. `RULINGS R124` item 4 requires the
 * suppression to be derived from the append-only log, and this is the arithmetic half of that
 * derivation; the log half is `alreadyTended` in ./surfacing.ts.
 *
 * A TEND IN THE FUTURE IS NOT EVIDENCE ABOUT ANY WINDOW, AND THE CODE NOW SAYS SO RATHER THAN
 * ASSUMING IT. This line used to read `tended >= window.from` under a comment asserting that
 * "`tended` is never in the future (a stone is placed at the act)". Nothing enforced that.
 * `lastTendedAt` folds `loop.stones`, stones arrive over sync from other devices
 * (`loopFromRow` in `@carn/sync`), and no clamp on `at` exists anywhere on that path — so one
 * stone stamped `now + 5d` by a device with a wrong clock satisfied `>= from` for every window
 * until the clock caught up, and a daily 07:00–09:00 chore was silently skipped for SIX
 * CONSECUTIVE DAYS. The midpoint arithmetic below was bypassed entirely, which matters because
 * the rule's own justification is that the rejected alternative "silently skips a day the user
 * was relying on": an unguarded future stone does exactly that, without bound.
 *
 * WHY THE GUARD IS HERE AND NOT A CLAMP AT INGEST. Rewriting an incoming stone's `at` would
 * change its `sessionStoneKey` and therefore its row id (`RULINGS R126` Part 2), so the next push
 * would write a SECOND row for a stone that already synced — duplicating history to defend
 * against a clock. The log is append-only and is left exactly as received; the DERIVATION declines
 * to read a future act as a past one, which costs nothing and is undone by the passage of time:
 * when `now` reaches the stone, it suppresses its own window and no more.
 *
 * THREE RESIDUALS, STATED RATHER THAN HIDDEN:
 *
 *   - a tend more than half a gap before its window still does not suppress it, so a chore done
 *     the previous afternoon for a morning window is asked for once. That is a trigger the user
 *     can ignore in one tap, it removes nothing, and it brings nothing into existence — the
 *     failure direction the no-overdue law cares about.
 *   - a future stone is invisible to the suppression until `now` passes it, so a chore genuinely
 *     tended on a device whose clock runs minutes fast may be asked for once more inside its
 *     window. One extra trigger is the cheap direction to be wrong in; six skipped days is not.
 *   - a loop carrying two NON-overlapping rhythm windows — a morning one and an afternoon one —
 *     is read as one chore that comes round in either, not as "do it twice a day": a morning tend
 *     suppresses the afternoon window, because the afternoon window's run-up begins at the
 *     midnight-ish midpoint of its own 22-hour gap. That reading is the authorised one. "Twice a
 *     day" is deferred vocabulary (`RULINGS R124` item 2, and RC1's note on what was deliberately
 *     not built), `lastTendedAt` is per LOOP and so cannot express "done once of two", and no
 *     shipped path produces a second rhythm condition — Sorting proposes none, and the seed
 *     carries exactly one. If the interval vocabulary ever lands, the shape that fixes this is to
 *     take the run-up from the loop's own preceding window end rather than from this condition's,
 *     which needs a "most recent window of this condition before T" that the current
 *     `evaluateCondition`-anchored pair deliberately does not have.
 */
export function tendSatisfiesWindow(
  condition: SurfacingCondition,
  now: number,
  tended: number,
): boolean {
  const window = currentRhythmWindow(condition, now);
  if (!window) return false;
  // An act cannot have happened yet. A stone stamped after `now` is a clock error, not evidence,
  // and reading it as evidence suppressed every window until it passed. See the docblock.
  if (tended > now) return false;
  if (tended >= window.from) return true;
  const previous = previousRhythmWindow(condition, now);
  if (!previous) return false;
  return tended >= previous.to + Math.floor((window.from - previous.to) / 2);
}

/**
 * A loop with no conditions is not "unsurfaceable"; it simply has no trigger.
 *
 * Permissive about `unknown`: an axis that cannot be judged never suppresses. Callers that
 * need to tell a real match from an unjudged one use `evaluateCondition` directly.
 */
export function conditionMatches(
  condition: SurfacingCondition,
  ctx: SurfacingContext,
): boolean {
  return evaluateCondition(condition, ctx) !== 'no-match';
}

// ---------------------------------------------------------------------------
// The Loop's append-only records
// ---------------------------------------------------------------------------

export type ClosureQuality = 'complete' | 'good-enough';

/**
 * A session stone. One per work session, added when a session ends by any means —
 * parked, interrupted, or closed. "Every work session added a stone — partial effort
 * made permanently visible, the anti-'it doesn't count unless finished' device"
 * (design-direction §6).
 *
 * THE CAIRN IS THIS LIST. Not a counter. design-direction §6 objection 1 calls the
 * streak/cairn distinction "the design team's most important inheritance", and the
 * distinction is exactly that a streak is a number that can go down while a cairn is a
 * pile that can only be added to. `sessionCount()` folds this; there is no field to
 * decrement.
 *
 * `tended` IS THE FOURTH, AND IT EARNS A STONE — but it is not a work session, and the
 * difference is load-bearing in exactly one direction (product-concept §8.1; the `tend`
 * transition in ./events.ts). A repeating loop never produces a `closed` record, so this list is
 * the only place a tend is recorded at all, and `lastTendedAt` ("last done Tuesday") and the
 * same-window suppression in ./surfacing.ts are both TOTAL only because the stone is placed
 * unconditionally, even for a two-second tap that timed no session.
 *
 * WHAT A TEND STONE MUST NOT DO IS INFLATE AN AGGREGATE "WORK SESSIONS" FIGURE. The reflection
 * report's personal cairn is rendered by `reflection.cairn.count` as "{count} work sessions" and
 * described as "one entry for every work session", and a tap is not a work session — four hundred
 * taps rendered as four hundred sessions is the fabricated work history design-direction §6
 * objection 1 makes the one number that must only ever go up honestly. So every fold that presents
 * a number as effort asks `isWorkSession` below, while `sessionCount()` — the LOOP's own pile, which
 * the user reads next to that loop and its "last done" line — counts every stone. Two different
 * things with one word, kept apart at the fold rather than in the log: `investedMs` is already
 * immune, because a tend adds zero duration.
 *
 * IT IS A PREDICATE RATHER THAN A HAND-COPIED CONDITION BECAUSE THREE SITES NEEDED IT AND ONE HAD
 * IT. `apps/mobile/src/screens/reflection/report.ts` inlined `stone.endedBy === 'tended'` at its
 * aggregate fold; `releaseObservation` (./compost.ts) and `captureSurvival`'s `engaged` tally
 * (./metrics.ts) did not have the rule at all, so a released chore reported "Work sessions: 400"
 * and computed a pattern claim about the user out of taps. See `isWorkSession`.
 */
export type SessionStone = {
  readonly loopId: string;
  readonly at: number;
  readonly durationMs: number;
  /** How the session ended. A stone is placed for all four; none is worth less. */
  readonly endedBy: 'parked' | 'interrupted' | 'closed' | 'tended';
};

/**
 * A session stone's identity, for deduplication.
 *
 * IT LIVES IN model.ts BECAUSE `SessionStone` DOES, and because the folds below now need it:
 * `standingStones` has to say WHICH stone a withdrawal names, and model.ts cannot import from
 * events.ts (events.ts imports from here). It was declared in events.ts and is re-exported from
 * there, so every existing importer is unaffected — the public surface of `@carn/core` is one
 * `export *` per module and this name resolves exactly as it did.
 *
 * Exported because several places need it and a second definition is the drift this repository
 * keeps catching: `mergeStones` (so a merge does not carry a stone twice), the personal cairn's
 * fold in `apps/mobile/src/screens/reflection/report.ts` (so an ended loop and the survivor that
 * absorbed it do not both contribute the same stone), and `standingStones` below. A stone has no
 * id — it is identified by `(loopId, at, durationMs, endedBy)` — and `SessionStone.loopId` is
 * deliberately never re-keyed by a merge, because re-keying would rewrite history to say the work
 * happened somewhere it did not.
 *
 * THE TUPLE IS NOT UNIQUE, AND THE COMMENT THAT SAID IT WAS WAS FALSE. It read: *"Two distinct
 * sessions on the same loop cannot share an `at`, because a session's stone is placed when the
 * session ends"* — an invariant `tend` broke on the day it was written, in a comment that was not
 * updated. A tend places a stone with NO session at all, so its `durationMs` is ALWAYS 0 and only
 * `at` distinguishes one tend from another; and `tend` is deliberately not idempotent, because two
 * taps mean the person did the thing twice. Two stones therefore CAN share this key, and CLAUDE.md
 * is flat about what may not follow from that: "History is append-only. Session stones accumulate."
 *
 * SO IDENTITY IS A KEY AND MULTIPLICITY IS COUNTED SEPARATELY. Two stones sharing this key are
 * value-identical in all four fields, hence interchangeable, so the only thing that can be lost is
 * HOW MANY — which `mergeStones` preserves, and which `stoneRowsOf` in `@carn/sync` preserves
 * across the wire by suffixing an ordinal onto the second and later row id within a key group.
 * `TendWithdrawal` names a stone by this key PLUS that same ordinal, for the same reason
 * (`RULINGS R126` Part 2: a stone's identity is not its timestamp).
 */
export function sessionStoneKey(stone: SessionStone): string {
  // The separator is written as an ESCAPE, never as a literal NUL byte. It was three raw NULs:
  // valid JavaScript, identical at runtime, and it made the file `data` rather than text — so
  // `grep`, `git diff` and every source-scanning gate under `tools/review/` skipped all of it in
  // silence. A separator that cannot occur in an id is the right choice; a separator that hides the
  // file from the tools auditing it is not. See RULINGS R118.
  return `${stone.loopId}\u0000${stone.at}\u0000${stone.durationMs}\u0000${stone.endedBy}`;
}

/**
 * THE USER TOOK A TEND BACK (`RULINGS R160`).
 *
 * WHY THIS EXISTS. `tend` was the only irreversible act on the loop detail surface. A mis-press
 * writes a permanent session stone AND suppresses the chore's next surfacing window — so the loop
 * does not come round, which is the exact failure the feature exists to prevent, achieved by
 * pressing the feature. The only fully reversible act on that surface (`remove`) was the only one
 * that asked first.
 *
 * IT APPENDS, IT NEVER DELETES. CLAUDE.md: "History is append-only. Session stones accumulate."
 * The withdrawn stone stays in `loop.stones` exactly as it was placed; this is a marker the
 * DERIVATIONS respect. `standingStones` is the one fold that reads it and every other fold reads
 * `standingStones`, so a fourth consumer inherits the rule rather than re-deciding it
 * (`RULINGS R125`'s species).
 *
 * IT NAMES A STONE BY KEY AND ORDINAL, NOT BY TIMESTAMP (`RULINGS R126` Part 2). Two tends in the
 * same millisecond are value-identical in all four of `sessionStoneKey`'s fields, so `at` alone
 * cannot say which one was taken back — and withdrawing "the stone at time T" would withdraw both.
 * `ordinal` is the stone's 0-based position among its indistinguishable siblings, exactly as
 * `stoneRowsOf` in `@carn/sync` numbers them for the wire, so the reference is stable across a
 * push and a pull.
 *
 * THERE IS NO `unwithdraw`, AND THAT IS NOT AN OVERSIGHT. This IS the undo; an undo of an undo is
 * a second tend, which the surface already offers and which places its own stone honestly. Nothing
 * here can be reached twice for the same stone either — `withdrawTend` refuses a stone that has
 * already been withdrawn, so a double press cannot make the cairn smaller by two.
 */
export type TendWithdrawal = {
  /** When the user took it back. */
  readonly at: number;
  /** `sessionStoneKey` of the withdrawn stone. */
  readonly stoneKey: string;
  /** Its 0-based position among stones sharing that key. `0` for the overwhelmingly common case. */
  readonly ordinal: number;
};

/** A withdrawal's own identity, so a marker cannot be appended twice for one stone. */
export function tendWithdrawalKey(withdrawal: {
  readonly stoneKey: string;
  readonly ordinal: number;
}): string {
  return `${withdrawal.stoneKey}\u0000#${withdrawal.ordinal}`;
}

/**
 * THE STONES THAT STILL COUNT — every stone in the log minus the tends the user took back
 * (`RULINGS R160`).
 *
 * ONE FOLD, SO THERE IS ONE PLACE THE RULE LIVES. `sessionCount`, `investedMs`, `lastTendedAt` and
 * `hasBeenWorked` all read this rather than `loop.stones` directly. `RULINGS R125`'s species is a
 * rule stated in one place and restated at each enforcement site; `isWorkSession` was made an
 * exported predicate for exactly that reason after three sites had hand-copied it, and this is the
 * same shape one layer up.
 *
 * `loop.stones` IS STILL THE LOG AND IS UNTOUCHED. Anything that wants the record as it was written
 * — the wire projection in `@carn/sync`, a future archive view — reads `loop.stones`. This function
 * answers a different question: what does the user's cairn CURRENTLY say, given what they have
 * taken back. Returning the array itself when there is nothing withdrawn is deliberate: the common
 * loop allocates nothing and the identity is preserved for a caller that compares by reference.
 */
export function standingStones(loop: Loop): readonly SessionStone[] {
  const withdrawals = loop.tendWithdrawals ?? [];
  if (withdrawals.length === 0) return loop.stones;

  const withdrawn = new Set(withdrawals.map(tendWithdrawalKey));
  const seen = new Map<string, number>();
  const out: SessionStone[] = [];
  for (const stone of loop.stones) {
    const key = sessionStoneKey(stone);
    const ordinal = seen.get(key) ?? 0;
    seen.set(key, ordinal + 1);
    if (withdrawn.has(tendWithdrawalKey({ stoneKey: key, ordinal }))) continue;
    out.push(stone);
  }
  return out;
}

/**
 * Terminal history, append-only (critique 1.2). `rewarm` appends a `reopened` record
 * rather than deleting the previous one, so a released loop's insight and a closure's
 * quality survive a one-tap rewarm — "released Loops are archived with their history
 * and searchable" (product-concept §5, objection 3).
 *
 * `quality` is REQUIRED on a closure (critique 7.11): "closed at good enough" is a
 * first-class ending (product-concept §6.4) and must not depend on a caller remembering
 * to pass it.
 */
/**
 * `releaseInsight` is a STRUCTURED `ReleaseObservation`, never rendered English
 * (RULINGS R28). Storing the sentence baked the register in force at release time into the
 * user's archive for ever, which implementation-brief §11's "literal-language mode covers
 * 100% of shipped strings" does not cover — and it undid D10's ruling that core returns a
 * fact rather than a sentence, one layer up. No migration was needed; nothing is in
 * production.
 *
 * FOUR KINDS, TWO OF WHICH ARE NOT ENDINGS:
 *
 *   `annotated` — an insight attached to the ending above it (RULINGS R30). Keeping a
 *                 release note used to call `release` a SECOND time, so the archive recorded
 *                 two endings for one decision and the surviving `lastTouchedAt` was when
 *                 the user finished reading a note. `annotateEnding` appends this instead:
 *                 the lineage grows, the ending does not, and any count over
 *                 `terminals.filter(kind === 'released')` stays correct.
 *   `merged`    — this loop was merged into another (RULINGS R32). It ENDS the loop — a
 *                 merged loop must leave the resumption surface — but it is deliberately
 *                 not a `closed` record, so §13's completion count cannot be inflated by
 *                 tidying up duplicates. `currentTerminal` flattens it to `closed`, because
 *                 `TerminalTemperature` has exactly two members and gains no third.
 */
export type TerminalRecord =
  | { readonly kind: 'closed'; readonly at: number; readonly quality: ClosureQuality }
  | {
      readonly kind: 'released';
      readonly at: number;
      readonly releaseInsight?: ReleaseObservation;
    }
  | { readonly kind: 'reopened'; readonly at: number }
  | {
      readonly kind: 'annotated';
      readonly at: number;
      readonly releaseInsight: ReleaseObservation;
    }
  | { readonly kind: 'merged'; readonly at: number; readonly intoLoopId: string };

/**
 * The three record kinds that ARE an ending. `reopened` reverses one and `annotated`
 * decorates one, so neither appears here.
 *
 * Declared as an array so a consumer can iterate it and so a `switch` over it can be proved
 * exhaustive — see `currentTerminal`, which is written as a `switch` with a `never` default
 * precisely so a sixth `TerminalRecord` kind is a build error rather than a silent fall-through
 * (RULINGS R78's audit found two consumers that widened silently).
 */
export const TERMINAL_ENDING_KINDS = ['closed', 'released', 'merged'] as const;
export type TerminalEndingKind = (typeof TERMINAL_ENDING_KINDS)[number];

/**
 * The flattened current ending, derived from the last terminal record.
 *
 * TWO FIELDS, TWO AUDIENCES (RULINGS R78). `state` is for DISPLAY: it is a
 * `TerminalTemperature`, which has exactly two members for ever, so a `merged` record
 * flattens into `closed` and the loop leaves the resumption surface. `kind` is the LOG kind
 * the state was derived from, and **every count partitions on `kind`** — a merge is
 * bookkeeping, not work finished, and flattening it into a completion statistic manufactures
 * a "this kind of work sticks" claim out of housekeeping (product-concept §13; the critique's
 * probe Q2, kept as a regression test).
 */
export type TerminalState = {
  readonly state: TerminalTemperature;
  /**
   * The append-only log's own word for this ending. `closed` and `released` flatten to
   * themselves; `merged` flattens to `closed` for `state` and stays `merged` here.
   */
  readonly kind: TerminalEndingKind;
  readonly at: number;
  readonly quality?: ClosureQuality;
  readonly releaseInsight?: ReleaseObservation;
  /** Set when this ending is a merge (RULINGS R32): the loop that survived. */
  readonly mergedIntoLoopId?: string;
};

/**
 * Did this loop stop existing separately rather than reach an outcome? (RULINGS R78.)
 *
 * The one predicate every §13 metric and every comparison universe filters on. It exists as a
 * named function rather than as `terminal?.mergedIntoLoopId !== undefined` inlined in five
 * places, because the rule is a product rule and a reader has to be able to find it.
 */
export function isMergedAway(loop: Loop): boolean {
  return currentTerminal(loop)?.kind === 'merged';
}

/**
 * An explicit, user-made settle marker (critique 2.5). Temperature is derived from
 * `lastTouchedAt`, so before this existed there was literally no way to accept
 * `bulkCompostOffer` — the offer in product-concept §11 was a button with nothing
 * behind it.
 *
 * Append-only, and it deliberately does NOT touch `lastTouchedAt`: tucking something
 * away must not rewrite when the user last worked on it.
 */
export type SettlementMarker = {
  readonly at: number;
  readonly kind: 'settled' | 'unsettled';
  readonly via: 'bulk-compost' | 'rewarm' | 'review';
};

/**
 * WHERE AN INFERENCE CAME FROM. Identifiers, not copy — the trigger line the Sorting card
 * renders is `sorting.basis.*` in `packages/copy`.
 *
 * §7.2 requires the user confirm every inference, and a user cannot confirm what they cannot
 * see the grounds for. The basis is that grounds, per part.
 */
export const PROPOSAL_BASES = [
  /** The user's own private dictionary: "'the girls' = the chickens" (§7.2). */
  'vocabulary',
  /** What a speech recogniser heard (implementation-brief §6). */
  'transcript',
  /** Whitespace, a trailing stop, an initial capital. Barely a suggestion, and says so. */
  'tidy',
  /** The coarse place label the device wrote onto the origin itself (brief §3). */
  'origin-place',
  /** A keyword table carried as locale data in `@carn/copy` (RULINGS R23). */
  'keyword',
  /** Significant-token overlap between two titles. */
  'title-overlap',
  /**
   * The line's HEAD WORD names a short bodily act — the evidence for a proposed energy fit
   * (`RULINGS R165`; product-concept §9.2's third surfacing axis). A verb table carried as locale
   * data in `@carn/copy`, on the same precedent `keyword` above is carried on (R23, D18).
   */
  'head-verb',
  /**
   * A model answered, through the AI proxy (`RULINGS R199`, R202; implementation-brief §6).
   *
   * The basis says *that a model ran* and nothing more, because that is the whole of what this
   * device can honestly report: the proxy returns text, not grounds, so any further account of
   * "why" would be the app narrating a reasoning process it did not observe. `RULINGS R200` caps
   * a part on this basis at the same `LOCAL_CEILING` every heuristic gets — Plus buys better
   * proposals, never louder ones.
   */
  'model',
  /**
   * The line's HEAD PHRASE presupposes a beginning — "Finish painting the fence" (product-concept
   * §7.2, §2 axiom 1; `docs/DEVIATIONS.md` §retrospective-capture RT1). A phrase table carried as
   * locale data in `@carn/copy`, on the same precedent `keyword` and `head-verb` are carried on
   * (R23, `R165`, D18).
   *
   * It is the one basis in this list whose evidence is the USER'S OWN CLAIM rather than the app's
   * reading of the world: "finish" presupposes a beginning in ordinary English, and the person who
   * typed it is the only witness to that beginning. The proposal quotes their word back at them.
   */
  'resumption-verb',
  /**
   * THE USER SAID SO. Not an inference at all, and that is the whole content of this basis.
   *
   * Authorised by product-concept §7.2 — the Sorting session is where triage happens, "at a moment
   * the user chooses", and the user "confirms with single swipes". A `ProposalPart` is the only
   * shape this application has for a claim about a loop that a verdict is then given on, and
   * `acceptProposal` is the only transition that applies one (`./sorting.ts`). So when the user
   * names a fact themselves rather than agreeing to a guess, the fact still travels as a part —
   * it just has no evidence behind it except them.
   *
   * IT IS NOT `vocabulary`, WHICH IS THE NEAREST THING AND IS DIFFERENT. `vocabulary` means "the
   * user taught us this phrase and we applied the rule they taught"; the app IS claiming
   * something there, from a stored substitution. This basis claims nothing. Confidence is 1 and
   * that is honest rather than boastful — §7.2's "confidence is shown honestly" cuts both ways,
   * and a chosen fact reported at 0.9 would be the app affecting doubt about a decision it did
   * not make.
   *
   * WHY IT MATTERS THAT IT IS RECORDED AT ALL, given that a part on this basis is proposed and
   * accepted in the same tick and is therefore never rendered on a card. The proposal is carried
   * on the `sorted` event, which is append-only and permanent (implementation-brief §2), and an
   * archive that could not tell "you put this on the Christmas list" apart from "we guessed it
   * belonged there and you agreed" has lost the distinction §7.2 objection 2 exists to protect.
   */
  'chosen',
] as const;
export type ProposalBasis = (typeof PROPOSAL_BASES)[number];

export const PROPOSAL_PART_KINDS = ['title', 'surfacing', 'list', 'duplicate', 'underway'] as const;
export type ProposalPartKind = (typeof PROPOSAL_PART_KINDS)[number];

/**
 * ONE INFERENCE. Its own kind, its own basis, its own confidence, independently acceptable
 * (RULINGS R31).
 *
 * WHY THE AGGREGATE `confidence` WENT. §7.2's clause is that "**every** inference is a
 * proposal confirmed by" the user, and that "confidence is shown honestly". One number over
 * three claims is neither: a 0.45 capitalisation tidy shipped under a 0.7 band because a
 * place label happened to be proposed in the same pass, and the capture critique reproduced
 * exactly that. There is no honest single number for three claims, so there is no field for
 * one.
 */
export type ProposalPart =
  | {
      readonly kind: 'title';
      readonly basis: ProposalBasis;
      readonly confidence: number;
      readonly value: string;
    }
  | {
      readonly kind: 'surfacing';
      readonly basis: ProposalBasis;
      readonly confidence: number;
      readonly value: readonly SurfacingCondition[];
    }
  | {
      readonly kind: 'list';
      readonly basis: ProposalBasis;
      readonly confidence: number;
      /**
       * `department` IS A `SectionId`, AND IT WAS A `BuiltInSectionId` UNTIL THIS PART GAINED A
       * SECOND PRODUCER (RULINGS R22, R125; product-concept §7.2).
       *
       * THE ARGUMENT THAT MADE IT NARROW, PRESERVED, BECAUSE IT IS STILL TRUE OF THE INFERENCE.
       * It was `string` here while R22 was closing departments into a union — and for as long as
       * the only thing that could produce this value was R23's shopping-word heuristic, the narrow
       * type was the honest one: that heuristic matches words against `BUILT_IN_SECTIONS` and has
       * no way to know a user called a section "Mum", so it can only ever name one of Carn's own
       * twelve supermarket departments. R125's own exclusion says the same from the other end —
       * *"no AI section assignment on a non-shopping list"* — and that exclusion is unaffected by
       * anything below. Nothing in this application infers a section a user authored, then or now.
       *
       * WHAT CHANGED. A part on the `chosen` basis is not an inference; it is the user naming the
       * list and the section themselves during a Sorting session, which is §7.2's own moment for
       * exactly that kind of decision. `BuiltInSectionId` cannot express "Sophie" — a minted
       * `sec-…` id — so the narrow type did not describe a value this part can now legitimately
       * carry, and a type that excludes a value the product produces is a type that lies. It is
       * widened rather than split into two part kinds because the VALUE is the same fact and
       * `acceptProposal` applies it the same way; the thing that differs is the `basis` beside
       * it, which is the field whose job is to say where a claim came from.
       *
       * SO THE INVARIANT MOVED FROM THE TYPE TO THE PRODUCER, and it is worth stating plainly
       * because the compiler no longer states it: a part on any basis EXCEPT `chosen` still only
       * ever carries one of the twelve, because `localHeuristics` is the only thing that mints
       * one and `departmentForLine` is the only thing it asks. R125's exclusion is enforced by
       * there being no code that could break it, which is where it was enforced before as well —
       * the narrow type was a restatement of that fact, never its cause.
       */
      readonly value: { readonly listId: string; readonly department?: SectionId };
    }
  | {
      readonly kind: 'duplicate';
      readonly basis: ProposalBasis;
      readonly confidence: number;
      /** The other loops this one might be a second copy of. */
      readonly value: readonly string[];
    }
  /**
   * IT WAS ALREADY UNDERWAY WHEN IT WAS CAUGHT (`docs/DEVIATIONS.md` §retrospective-capture RT1;
   * product-concept §7.2, §2 axiom 1, §6).
   *
   * `value` IS THE EVIDENCE, NOT THE CLAIM, and it is the only part where those differ. The other
   * four propose a value to write — a title, some conditions, a list, a set of twins. This one
   * proposes a FACT about the past that has no value to write, so its `value` carries the head
   * phrase that fired ("finish"), which is what the card shows the user, in their own word. A part
   * with no value at all would be dropped by `makeProposal`'s `hasValue` filter, and a part whose
   * value the surface had to invent would be the app narrating evidence it did not have.
   *
   * ACCEPTING IT APPLIES NOTHING BY ITSELF, exactly as `duplicate` applies nothing by itself
   * (RULINGS R32). Recording prior work needs an `Ids` and, optionally, a date the user has not
   * been asked for yet, so `acceptProposal` records the verdict and echoes it, and
   * `recordPriorInterrupt` in ./events.ts is what writes. A fact about the user's own history must
   * never land as a side effect of a swipe on a different claim.
   */
  | {
      readonly kind: 'underway';
      readonly basis: ProposalBasis;
      readonly confidence: number;
      /** The head phrase that fired, lowercased. The user's own word, quoted back. */
      readonly value: string;
    };

/**
 * The Sorting proposal (product-concept §7.2). "AI never acts silently — every
 * inference is a proposal confirmed by swipe, confidence is shown honestly."
 *
 * Up to four independent claims, each with its own verdict pair on the card, each accepted
 * or refused on its own (RULINGS R31). `acceptProposal(loop, now, acceptedKinds)` applies
 * only what was accepted.
 */
export type SortingProposal = {
  readonly proposedAt: number;
  readonly parts: ReadonlyArray<ProposalPart>;
  /**
   * Shared by every loop the model thinks is the same thing ("captured three times").
   *
   * A GROUP KEY, not a claim — which is why it is not inside the `duplicate` part.
   * `duplicateGroups()` folds on it, so it has to be derivable from the members in any
   * order (see `apps/mobile`'s `duplicateGroupIdFor`).
   */
  readonly duplicateGroupId?: string;
};

/** The accepted value for one kind, or `undefined` when the proposal makes no such claim. */
export function proposalPart<K extends ProposalPartKind>(
  proposal: SortingProposal | undefined,
  kind: K,
): Extract<ProposalPart, { kind: K }> | undefined {
  if (!proposal) return undefined;
  return proposal.parts.find((p): p is Extract<ProposalPart, { kind: K }> => p.kind === kind);
}

// ---------------------------------------------------------------------------
// Work that happened before Carn knew about it
// ---------------------------------------------------------------------------

/**
 * IT HAD ALREADY STARTED WHEN IT WAS CAUGHT — the retrospective capture
 * (`docs/DEVIATIONS.md` §retrospective-capture RT1; product-concept §2 axiom 1, §6, §7.2).
 *
 * THE DEFECT IT CLOSES. `hasBeenWorked` below asks the loop's own cairn and capsule log, and for a
 * line typed the day after the work stopped it answers `false` — so `reentryCopy` offers to
 * *start* a fence the user has half painted, and `CapsuleDetail` says "nothing recorded about
 * where you stopped" about work they can see from the kitchen window. That is a false statement
 * about the user's own history, made by the app whose entire thesis (§1) is "the moment a task gets
 * interrupted or abandoned, rather than the moment it gets created". The loop's history began at
 * capture; the user's began weeks earlier.
 *
 * IT IS TESTIMONY, NOT AN INFERENCE. Nothing writes this except a person answering a question
 * about their own past. The Sorting heuristic may PROPOSE it (`ProposalPart`'s `underway` kind) and
 * §7.2's confirmation path is what turns the proposal into this record; there is no path from a
 * guess to a written `PriorInterrupt`.
 *
 * `startedAt` IS OPTIONAL AND THE OPTIONALITY IS THE DESIGN. §6.1's tiering "assumes the user gives
 * us nothing and degrades gracefully upward", and the person this feature exists for is precisely
 * the person who cannot remember which Tuesday it was. Saying "yes, I'd started it" with no date is
 * a complete record: it is what `hasBeenWorked` reads, and it is all the resumption surface needs
 * to stop offering to start something. A date, when the user has one, is enrichment on top —
 * appended by a second call to the same transition, per-field last-write-wins like every other
 * column (implementation-brief §2).
 *
 * IT IS NOT A SIXTH `Temperature` AND NOT A SIXTH TERMINAL. `TEMPERATURES` has exactly five
 * members and `TERMINAL_ENDING_KINDS` exactly three, forever. This is a fact about the past that
 * changes what the app SAYS, in the same shape as `hasBeenWorked` itself: "the state vocabulary
 * does not grow, and the COPY LAYER branches on a derived fact so that what the app says is true".
 *
 * IT IS NOT AN OVERDUE STATE AND MUST NEVER BECOME ONE (CLAUDE.md; product-concept §5). A start
 * date is a date in the PAST and nothing derives urgency, prominence or colour from it — it is not
 * a `LoopDate`, it never reaches `deadlineProminence`, and a loop started a year ago is exactly as
 * warm as one started this morning. "Nothing is ever taken away by time" holds: this only ever ADDS
 * history the app did not previously have.
 */
export type PriorInterrupt = {
  /** When the user told us. Always present — this record cannot exist without a person making it. */
  readonly recordedAt: number;
  /**
   * The start of the local day the user named, or absent when they did not name one.
   *
   * THE START OF THE DAY, NOT AN INSTANT, and it is the mirror of `RULINGS R39`'s reasoning for a
   * hard deadline: there the app stores the END of the chosen day, because for a thing still to
   * come the latest instant is the one that claims nothing the user did not say. For a thing
   * already done the claim runs the other way, so the earliest instant of the day is the honest
   * one. Either way the app renders the DAY back, never the instant (R39).
   *
   * NEVER IN THE FUTURE. `recordPriorInterrupt` drops a `startedAt` after `now` rather than
   * clamping it: nothing has started in the future, and a clamped value would be a date the user
   * did not give presented as one they did.
   */
  readonly startedAt?: number;
};

// ---------------------------------------------------------------------------
// The Loop
// ---------------------------------------------------------------------------

/**
 * Every field is `readonly` (critique 1.5). Warming a loop happens only inside a named
 * transition in `events.ts`; there is no exported setter for `lastTouchedAt`, so "make
 * this warm" cannot be written as a two-line change.
 *
 * There is deliberately no `active` field (critique 1.6): "which loop am I on right
 * now" is ephemeral session state that implementation-brief §5 fences into Zustand, and
 * a per-loop boolean makes two-simultaneously-active representable. It is passed in as
 * `DerivationOptions.activeLoopId`.
 */
export type Loop = {
  readonly id: string;
  /**
   * NULLABLE, and that is the fix rather than the omission (RULINGS R27).
   *
   * A voice-or-photo capture with no typed line used to be given a placeholder title from
   * the copy catalogue at capture time, which baked the register in force that day into the
   * user's archive for ever. implementation-brief §11's "literal-language mode covers 100%
   * of shipped strings" is a definition-of-done clause, and a warm-register placeholder
   * frozen in an archive is a shipped string literal mode does not cover.
   *
   * So the domain says "this has no name of its own" and every render site resolves it
   * through `capture.voiceTitle` / `capture.photoTitle`, selected by `origin.channel`, at
   * READ time. `acceptProposal` remains the only transition that sets a real title. This
   * also makes the blank card impossible by construction rather than by convention.
   */
  readonly title: string | null;
  /** Free text the user added later. Capture itself demands nothing (§7.1). */
  readonly note?: string;
  /**
   * ONE RAW URL, ATTACHED AFTER THE FACT (`RULINGS R178`).
   *
   * RAW, NEVER UNFURLED. There is no title field beside it and there must never be one: fetching a
   * title is a network read on a device whose architecture is "no user interaction ever waits on
   * the network" (implementation-brief §2), and it tells a third party which page this user is
   * looking at, from their device, unprompted — data minimisation is a privacy invariant with force
   * of law (§3). Doing it server-side only moves the disclosure onto our own infrastructure. The
   * render site draws the URL itself, with a tap to open.
   *
   * ONE, NOT MANY. A list of links is an append-only child table, a sync group, an editing surface
   * and a place for it on the loop's page. This is one field beside the note that already holds
   * strings, and it is per-field last-write-wins like every other column (§2).
   *
   * IT IS NOT A CAPTURE CHANNEL. `CaptureChannel` stays a closed union of eight and `Origin` stays
   * never-editable — "share-sheet from any app" already covers a URL arriving as a NEW loop, and
   * this is about attaching one to a loop that exists. It travels where the loop travels, which is
   * a field's ordinary behaviour and does not widen §10's three-gesture ceiling.
   */
  readonly link?: string;
  readonly origin: Origin;
  /** Bumped by any interaction. The recency input to temperature derivation. */
  readonly lastTouchedAt: number;
  /** Append-only. Empty = still open. See `currentTerminal`. */
  readonly terminals: readonly TerminalRecord[];
  readonly date?: LoopDate;
  readonly surfacing: readonly SurfacingCondition[];
  /** Append-only capsule log. The newest is the one the home surface renders. */
  readonly capsules: readonly Capsule[];
  /** Append-only. THE CAIRN. `sessionCount()` and `investedMs()` fold this. */
  readonly stones: readonly SessionStone[];
  /** Append-only settle/unsettle markers. Absent = never settled. */
  readonly settlements?: readonly SettlementMarker[];
  /**
   * Append-only tend withdrawals (`RULINGS R160`). Absent = nothing has been taken back.
   *
   * A MARKER, NOT A DELETION. See `TendWithdrawal` — the stone stays in `stones` for ever and this
   * list is what the folds consult. Adding a member here can only ever make the visible cairn
   * smaller by one; nothing removes a stone from the log, on this device or over the wire.
   */
  readonly tendWithdrawals?: readonly TendWithdrawal[];
  /** The user's own list membership, e.g. a shopping list id. */
  readonly listId?: string;
  /** Unsorted items still surface by best-guess context, just with lower confidence (§7.2). */
  readonly sorted: boolean;
  /**
   * IT HAD ALREADY STARTED WHEN IT WAS CAUGHT. Absent on every loop nobody has said that about,
   * which is the shape that matters: a loop carries no such claim until a person makes one, so
   * nothing in the model can decide on the user's behalf that they had started something. See
   * `PriorInterrupt`.
   */
  readonly priorInterrupt?: PriorInterrupt;
  /** The pending AI proposal, present until the user swipes (§7.2). */
  readonly proposal?: SortingProposal;
  /**
   * THE USER TOOK IT BACK (docs/DEVIATIONS.md D32).
   *
   * Absent on every loop that has not been removed, which is the shape that matters: a loop
   * carries no removal state until a person makes one, so nothing in the model can drift into
   * removing something on the user's behalf.
   *
   * It is NOT a terminal record and never becomes one. `closed`, `released` and `merged` are
   * outcomes — things that happened to work the user did — and `TERMINAL_ENDING_KINDS` is what
   * every §13 metric partitions on. A removal is the opposite claim: this was never work, so it
   * must not land in the archive as an ending, must not be counted as a decision, and must not
   * flatten into a `TerminalTemperature`. `Temperature` still has exactly five members and
   * `TERMINAL_ENDING_KINDS` still has exactly three.
   *
   * A TOMBSTONE, NOT A DELETE (implementation-brief §2: "tombstones instead of deletes"). The
   * loop row survives, its history survives, and `restore` clears this field — so removal is
   * reversible for as long as the row is on the device, and the sync engine has something to
   * propagate. What the field changes is REACHABILITY: `apps/mobile`'s repository does not
   * return a discarded loop from any read, so no surface, count, metric or search can see one.
   */
  readonly discardedAt?: number;
};

/**
 * Has the user removed this loop? (docs/DEVIATIONS.md D32.)
 *
 * The one predicate every read boundary filters on, named rather than inlined as
 * `loop.discardedAt !== undefined` — the same reason `isMergedAway` exists: the rule is a
 * product rule and a reader has to be able to find it.
 */
export function isDiscarded(loop: Loop): boolean {
  return loop.discardedAt !== undefined;
}

/** Everything the user has not removed, in the order given. */
export function presentLoops(loops: readonly Loop[]): readonly Loop[] {
  return loops.filter((loop) => !isDiscarded(loop));
}

// ---------------------------------------------------------------------------
// The thirty-day purge of a REMOVED loop (docs/RULINGS.md R135)
// ---------------------------------------------------------------------------

/**
 * HOW LONG A REMOVED LOOP'S CONTENT STAYS ON THE DEVICE (RULINGS R135).
 *
 * THIS IS THE ONE OBJECT THE "NOTHING IS EVER TAKEN AWAY BY TIME" LAW WAS NEVER ABOUT, and
 * R135's closing paragraph is a prohibition rather than a caveat: "Anyone tempted to generalise
 * this purge to any other object should read this paragraph as a prohibition." The law's subject
 * is the app — no decay, no expiry, no streak, no penalty for absence — and its objects are the
 * user's intentions and their history. A loop the user EXPLICITLY REMOVED is neither, and thirty
 * days of total undo is the mitigation. Nothing else in Carn expires, and nothing else may.
 *
 * THIRTY, and the number is deliberately the same one: implementation-brief §2 objection 2's
 * recovery journal is thirty days (`RECOVERY_JOURNAL_DAYS`), so the product has ONE retention
 * number rather than two clocks a support conversation has to disambiguate. It is written as a
 * reference to that constant rather than as a second `30` for exactly that reason.
 */
export const REMOVED_LOOP_PURGE_DAYS = RECOVERY_JOURNAL_DAYS;

const PURGE_AFTER_MS = REMOVED_LOOP_PURGE_DAYS * 24 * 60 * 60 * 1000;

/**
 * Has this loop been removed for longer than the undo window? (RULINGS R135.)
 *
 * A loop that was never removed is never due — `discardedAt` absent is the shape that matters
 * (see `Loop.discardedAt`), so nothing here can decide to purge something the user still holds.
 */
export function isPurgeDue(loop: Loop, now: number): boolean {
  return loop.discardedAt !== undefined && loop.discardedAt < now - PURGE_AFTER_MS;
}

/** What a purge takes off the disk as well as out of the row (RULINGS R135). */
export type PurgedLoop = {
  /** The row that stays: `{ id, discardedAt }` plus the emptiest legal value for every field. */
  readonly loop: Loop;
  /**
   * The media the row used to point at — `voiceNoteUri` and `photoUri` off every capsule.
   *
   * Returned rather than deleted here because this package is pure (CLAUDE.md: "no React, no
   * I/O, no platform imports"), and because the caller is the only layer that knows whether
   * this device has a filesystem at all. **A purge that leaves the audio on disk is not a
   * purge** (R135 build consequence 2), so a caller that ignores this list has not purged.
   */
  readonly mediaUris: readonly string[];
};

/**
 * Empty a removed loop, keeping the sync tombstone (RULINGS R135).
 *
 * WHAT SURVIVES AND WHY IT IS EXACTLY THIS. R135 keeps `{ id, discardedAt }` — the id because a
 * tombstone with no key is not a tombstone, and `discardedAt` because that field IS the tombstone
 * (`loopToRow` maps it to `SyncMeta.deletedAt`, which is what the server reads). Everything else
 * that carries the user's words, their places, the people they named, their history and their
 * media is gone.
 *
 * THE FIELDS THAT CANNOT SIMPLY BE ABSENT GET THEIR EMPTIEST LEGAL VALUE, because the row is still
 * a `Loop` and every read boundary in the app still type-checks against one. `title` is `null`
 * (R27's "this has no name of its own"), the append-only logs are empty arrays, `sorted` is false.
 * `origin.capturedAt` and `lastTouchedAt` collapse onto `discardedAt`: the time of day the user
 * captured something is a fact about their day, and the purge keeps ONE timestamp, not three.
 *
 * `origin.channel` IS KEPT, and it is the one judgement call here. There is no neutral member of
 * `CaptureChannel` to write, and inventing one — recording a voice capture as `text` — would make
 * the row state something false about the user rather than nothing. A closed eight-value enum with
 * no content in it is the smaller cost, and it is stated here rather than left to be discovered.
 *
 * IDEMPOTENT. Purging an already-purged loop returns the same row and no media, so a sweep that
 * runs on every launch does one write the first time and nothing afterwards (R135 build
 * consequence 4).
 */
export function purgeLoop(loop: Loop): PurgedLoop {
  const discardedAt = loop.discardedAt;
  // Not a purgeable loop at all: hand it back untouched rather than manufacture a tombstone.
  if (discardedAt === undefined) return { loop, mediaUris: [] };

  const mediaUris = loop.capsules.flatMap((capsule) =>
    [capsule.voiceNoteUri, capsule.photoUri].filter((uri): uri is string => uri !== undefined),
  );

  return {
    loop: {
      id: loop.id,
      title: null,
      origin: { channel: loop.origin.channel, capturedAt: discardedAt },
      lastTouchedAt: discardedAt,
      terminals: [],
      surfacing: [],
      capsules: [],
      stones: [],
      sorted: false,
      discardedAt,
    },
    mediaUris,
  };
}

// ---------------------------------------------------------------------------
// Derived aggregates — LAW 3
// ---------------------------------------------------------------------------

/**
 * The title to COMPARE, as opposed to the title to DRAW (RULINGS R27).
 *
 * `undefined` when the loop has no name of its own. Duplicate detection and vocabulary
 * substitution must skip such a loop rather than fall back to a placeholder: comparing
 * placeholders made **every pair of undeclined voice notes a perfect title match**, so a user
 * who recorded three things in the barn was told all three were the same thing — the confident
 * wrong guess §7.2 objection 2 says loses this audience at the first offence.
 *
 * It lives here, in the domain, rather than beside the RENDER-time resolver in `@carn/ui`:
 * this function reads no catalogue and renders nothing, and both `apps/mobile`'s heuristics
 * (which must stay free of any UI import) and any future server-side pre-triage need it.
 */
export function comparableTitle(loop: Loop): string | undefined {
  return loop.title ?? undefined;
}

/**
 * The cairn's height. A fold, so it cannot be decremented (critique 1.1).
 *
 * It folds `standingStones`, not `loop.stones` (`RULINGS R160`). A withdrawn tend stops counting
 * toward the visible cairn and is NOT deleted from the log — the only way this number goes down is
 * a person taking back a press they just made, which is the user acting on their own record and is
 * not the "taken away by time" the law is about (design-direction §6 objection 1; the same
 * distinction docs/DEVIATIONS.md D32 objection 2 draws for removal).
 */
export function sessionCount(loop: Loop): number {
  return standingStones(loop).length;
}

/**
 * A TAP IS NOT A WORK SESSION (product-concept §8.1; `RULINGS R124` item 2; `RULINGS R125`).
 *
 * Every stone is history and every stone stays in the log — this decides only whether a stone may
 * be counted into a number PRESENTED AS EFFORT, or fed to arithmetic that makes a claim about how
 * the person works. A tend is a two-second tap with `durationMs: 0` placed unconditionally, so a
 * chore tended four hundred times carries four hundred stones and no work sessions at all.
 *
 * IT IS ONE EXPORTED PREDICATE BECAUSE THERE ARE THREE CONSUMERS AND THERE WAS ONE HAND-COPY.
 * `apps/mobile/src/screens/reflection/report.ts` had `if (stone.endedBy === 'tended') continue`
 * inlined at its aggregate-cairn fold, and both `releaseObservation` (./compost.ts) and
 * `captureSurvival`'s `engaged` tally (./metrics.ts) needed the same rule and did not have it — so
 * releasing a chore tended four hundred times rendered "Work sessions: 400" and computed
 * `comparativeObservation`'s likelihood ratio, a pattern claim about the user's habits, out of
 * taps. That is `RULINGS R125`'s species exactly: a rule stated in one place with enforcement sites
 * that restate it. One name, so a fourth site inherits the rule rather than re-deciding it.
 *
 * THE LOOP'S OWN CAIRN IS `sessionCount` AND IT KEEPS COUNTING EVERYTHING. A repeating chore's pile
 * IS its history of being done, read beside its "last done Tuesday" line, and R124 places the stone
 * there deliberately. The distinction is between the pile and a number rendered as "work sessions".
 */
export function isWorkSession(stone: SessionStone): boolean {
  return stone.endedBy !== 'tended';
}

/**
 * HAS ANY WORK EVER HAPPENED ON THIS LOOP? (product-concept §6.3; design-direction §1;
 * docs/DEVIATIONS.md top/D38.)
 *
 * A loop that was caught and never touched again has an empty cairn and an empty capsule log,
 * and the surface built for RE-ENTRY was telling the user to re-enter it: the card's one primary
 * action read "Pick it up" / "Resume task" and its hint promised "opens it where you left off",
 * about a thing they had never opened, never left, and never been anywhere in. That is a false
 * statement about the user's own work, made in the register whose entire purpose is precision.
 * §6.3's claim is about a capsule — "the photo of the half-done fence… 'next: get the 13mm from
 * the jacket'" — and there is no half-done fence here.
 *
 * IT IS A FOLD, AND THAT IS THE WHOLE DESIGN. There is no `unstarted` state and nothing is filed
 * at capture: a captured loop carries NO state, and the ABSENCE of stones and capsules is the
 * fact. So it cannot drift out of step with the log, it cannot be set by anything, and CLAUDE.md's
 * "capture is under two seconds with zero decisions" is untouched — this adds nothing to the
 * capture path.
 *
 * THE FOLD HAS A THIRD INPUT SINCE THE RETROSPECTIVE CAPTURE, AND THE PARAGRAPH ABOVE USED TO SAY
 * "there is no `started` field" (`docs/DEVIATIONS.md` §retrospective-capture RT1). `priorInterrupt`
 * is one, and the sentence was rewritten rather than left standing, because CLAUDE.md's rule is
 * that a comment citing a section marks a load-bearing line and a comment that overstates is worse
 * than none. Three things keep the design intact:
 *
 *   - IT IS STILL NOT SET AT CAPTURE. `capture` in ./events.ts does not write it and cannot; the
 *     two-second rule and its zero decisions are untouched. It is written at Sorting, or later,
 *     which is where §7.2 puts every decision capture refuses to demand.
 *   - IT IS NOT AN INFERENCE. The heuristic PROPOSES; a person answers. See `PriorInterrupt`.
 *   - IT CANNOT DRIFT, because there is nothing for it to drift from: unlike a cached terminal
 *     state it summarises no log, it IS the record, and the only other thing that could contradict
 *     it — a stone or a capsule — makes this fold answer `true` anyway.
 *
 * WITHOUT IT THIS FUNCTION MADE THE FALSE STATEMENT IT EXISTS TO PREVENT, IN THE OTHER DIRECTION.
 * "Finish painting the fence", typed on Tuesday about work stopped on Sunday, has no stone and no
 * capsule, so the card offered "Start it" for a fence that is half painted — the same defect this
 * docstring opens with, mirrored.
 *
 * IT IS NOT A SIXTH `Temperature`, AND MUST NEVER BECOME ONE. `TEMPERATURES` has exactly five
 * members, forever. This is the same shape as `TemperatureReason`, one screen above: the state
 * vocabulary does not grow, and the COPY LAYER branches on a derived fact so that what the app
 * says is true. A never-worked loop is still `warm` and still sits at the top — where it belongs,
 * because it is the most recent thing the user cared about. Only the words change.
 *
 * ANY STONE COUNTS, INCLUDING A TEND — deliberately, and this is the one place `isWorkSession`
 * above must NOT be reused. A chore tended four hundred times has been done four hundred times;
 * telling its owner it has not been started would be the same false statement in the other
 * direction. The distinction that predicate draws is about numbers PRESENTED AS EFFORT, and this
 * is not a number.
 *
 * A CAPSULE COUNTS WITHOUT A STONE because parking from a surface that timed no session places a
 * Tier-0 capsule and no stone (`park` in ./events.ts). There IS a bookmark to return to at that
 * point, so re-entry is exactly the right word.
 *
 * WHAT THIS CANNOT SEE is the session in progress right now: `resume` places nothing until the
 * work ends by some means, and "which loop am I on" is ephemeral session state that
 * implementation-brief §5 fences into Zustand and keeps off the `Loop` (see the note on `Loop`
 * about the absent `active` field). A caller that has `activeLoopId` must treat the active loop
 * as started — `LoopCard` takes `isActive` and does — or the card will offer to start the thing
 * the user is standing in front of.
 */
export function hasBeenWorked(loop: Loop): boolean {
  // `standingStones`, not `loop.stones` (`RULINGS R160`): a loop whose ONLY stone was a mis-pressed
  // tend the user took back has not been worked, and telling them it has is the same false
  // statement in the other direction from the one this function was written to stop.
  if (standingStones(loop).length > 0 || loop.capsules.length > 0) return true;
  // The user's own testimony that it was already underway when they caught it. There is no
  // withdrawal marker to consult here as there is for a tend: taking the claim back is the same
  // control pressed again, which clears the field outright (`recordPriorInterrupt`).
  return loop.priorInterrupt !== undefined;
}

/**
 * DID THE USER SAY THIS ONE WAS ALREADY UNDERWAY WHEN THEY CAUGHT IT?
 *
 * Named rather than inlined as `loop.priorInterrupt !== undefined`, for the reason `isDiscarded`,
 * `isMergedAway` and `isRepeating` are named: it is a PRODUCT rule and every site that depends on
 * it has to be findable. `hasBeenWorked` above is the broader question — has any work happened at
 * all — and answers `true` for a loop that was parked yesterday. This one is narrower and is what a
 * surface asks when it wants to say the specific thing: you had already started this before Carn
 * ever saw it.
 */
export function wasUnderwayAtCapture(loop: Loop): boolean {
  return loop.priorInterrupt !== undefined;
}

// ---------------------------------------------------------------------------
// Repeating work (product-concept §8.1; docs/OPEN-QUESTIONS.md "Recurring work")
// ---------------------------------------------------------------------------

/**
 * Does this loop come round? — i.e. does it carry a rhythm condition.
 *
 * Named rather than inlined as `loop.surfacing.some(c => c.axis === 'rhythm')` for the same
 * reason `isMergedAway` and `isDiscarded` are named: it is a PRODUCT rule (a repeating loop is
 * offered `tend` instead of only `close`, §8.1) and a reader has to be able to find every site
 * that depends on it.
 *
 * A PROPOSED RHYTHM COUNTS, and that is the deliberate answer to the only real question here.
 * ./surfacing.ts already matches proposed conditions — product-concept §7.2, DECIDED: "unsorted
 * items still surface by best-guess context, just with lower confidence" — so a loop whose
 * rhythm is a guess WILL come round on Sunday whether or not the user has swiped yes. If `tend`
 * were withheld until the swipe, the only ending available on Sunday would be `close`, which is
 * terminal, and the loop that the app itself just brought round would be destroyed by being
 * done: exactly the defect §8.1 is incomplete without. Consistency with the surfacing rule is
 * therefore not a stylistic preference, it is the thing that stops the two halves disagreeing
 * about the same loop on the same morning.
 *
 * The cost is that the app may offer "this comes round" on an inference the user has not
 * confirmed. That cost is bounded and already priced: §7.2's own answer to a wrong guess is one
 * swipe, the affordance is a past-tense statement about an act rather than a claim about the
 * future, and a surface that wants to present a guess more tentatively than a fact has
 * `SurfacedCandidate.proposed` and `proposalPart(loop.proposal, 'surfacing')` to do it with.
 * Nothing here writes anything: confirming a rhythm is still only `acceptProposal`.
 */
export function isRepeating(loop: Loop): boolean {
  if (hasConfirmedRhythm(loop)) return true;
  const part = proposalPart(loop.proposal, 'surfacing');
  return part !== undefined && part.value.some((condition) => condition.axis === 'rhythm');
}

/**
 * Does this loop carry a rhythm the USER has confirmed? (`RULINGS R204`.)
 *
 * `isRepeating`'s narrower half, and the two are deliberately different questions. `isRepeating`
 * answers "will this come round", which a PROPOSED rhythm is enough for, because
 * `evaluateSurfacing` already matches a proposed condition (product-concept §7.2). This answers
 * "is there a condition on this loop the user put there", which is the only question a control
 * that WRITES the condition may ask.
 *
 * IT EXISTS BECAUSE THE MANUAL TOGGLE HAS THREE STATES AND `isRepeating` ONLY SEES TWO
 * (R204 build consequence 2). A loop whose rhythm is a Sorting guess answers `true` to
 * `isRepeating` and `false` here, and that gap is exactly the case a toggle must not get wrong:
 * "stop it coming round" on a proposed-only rhythm would filter `loop.surfacing`, remove nothing,
 * leave `isRepeating` true, and write nothing — the live-looking control that does nothing which
 * `LoopDetail`'s `comesRound` docblock records as already shipping once on this surface. The
 * toggle therefore renders nothing in that state and Sorting keeps the decision, which is the one
 * owner it has ever had (`RULINGS R128` Part 1).
 *
 * Named rather than inlined for `isRepeating`'s own reason: it is a PRODUCT rule about which
 * conditions the user authored, and a reader has to be able to find every site that depends on it.
 */
export function hasConfirmedRhythm(loop: Loop): boolean {
  return loop.surfacing.some((condition) => condition.axis === 'rhythm');
}

/**
 * When was this last done? — the newest `tended` stone's instant, or `undefined`.
 *
 * This is the whole of the card's one line, "last done Tuesday" (docs/OPEN-QUESTIONS.md
 * "Recurring work", item 6). Derived from the cairn, which is append-only, so there is no
 * "next due" field anywhere and nothing to keep in step — and it is TOTAL because `tend`
 * places a stone unconditionally, even for a two-second tap with no timed session. A
 * conditional stone would have made this function silently lie about work that happened.
 *
 * NOTHING IN THIS FILE, OR ANY OTHER, MAY COUNT CONSECUTIVE WINDOWS. No `tendStreak`, no
 * `windowsHit / windowsElapsed`, no "12 of the last 14", no percentage, no "on track". Not as
 * a private helper, not as a debug read-out, not behind a setting. design-direction §6
 * objection 1 locates the streak/cairn distinction precisely — "a streak is a number that can
 * go down, a cairn is a pile that can only be added to" — and calls it "the design team's most
 * important inheritance"; CLAUDE.md states it flatly: "There is no streak anywhere." A missed
 * window is not represented in this model AT ALL, which is exactly what makes the whole
 * repeating-work design legal under the no-overdue law: there is no object to be late and no
 * gap to render. Any function that divides tends by elapsed windows re-creates the missed
 * window as a number, and the number can go down. If you are here to add one, the answer is no.
 *
 * `asOf` IS THE ANSWER TO A FUTURE-DATED STONE, and it exists because the suppression asks this
 * function for "the newest tend" and then tests exactly one instant. Stones arrive over sync from
 * other devices with no clamp on `at` (`loopFromRow` in `@carn/sync`), so one stone stamped
 * `now + 5d` by a wrong clock became "the newest tend" and shadowed a real tend from this morning.
 * `lastTendedAt(loop, { asOf: now })` asks the log the question the caller actually has — "what is
 * the newest tend that has HAPPENED" — and the un-passed form still reports the log's own newest,
 * which is right for the card's "last done Tuesday" line: the log is displayed as it is, never
 * silently edited. Nothing is filtered out of the log itself, and no stone is rewritten:
 * `RULINGS R126` Part 2 makes re-keying a synced stone the one repair that is forbidden.
 */
export function lastTendedAt(loop: Loop, opts?: { asOf?: number }): number | undefined {
  const asOf = opts?.asOf;
  let latest: number | undefined;
  // `standingStones`, not `loop.stones` (`RULINGS R160`). This is the log half of the same-window
  // suppression, so a withdrawn tend has to become invisible here or the window it suppressed
  // stays shut — and reopening it is the whole point of the withdrawal. It is also the card's
  // "last done Tuesday" line, which must not report a press the user took back.
  for (const stone of standingStones(loop)) {
    if (stone.endedBy !== 'tended') continue;
    if (asOf !== undefined && stone.at > asOf) continue;
    if (latest === undefined || stone.at > latest) latest = stone.at;
  }
  return latest;
}

/**
 * Every rhythm condition this loop could come round on — confirmed and proposed together.
 *
 * Both lists, for the reason `isRepeating` reads both: `evaluateSurfacing` matches a proposed
 * condition too (product-concept §7.2, "unsorted items still surface by best-guess context"), so a
 * loop the app itself brings round on Sunday must be describable and withdrawable on Sunday whether
 * or not the user has swiped yes.
 */
function rhythmConditionsOf(loop: Loop): readonly SurfacingCondition[] {
  const proposed = proposalPart(loop.proposal, 'surfacing')?.value ?? [];
  return [...loop.surfacing, ...proposed].filter((condition) => condition.axis === 'rhythm');
}

/**
 * THE TEND THE USER MAY TAKE BACK, or `undefined` (`RULINGS R160`).
 *
 * The withdrawal is an UNDO, and this is where its scope is decided. It answers with the newest
 * standing tend on the loop, and ONLY while that tend is still the last thing that happened to the
 * loop — `stone.at === loop.lastTouchedAt`, which is exactly what `tend`'s own `touched()` wrote.
 *
 * WHY THAT BOUND, AND WHAT IT BUYS. Three properties, none of which needs a clock:
 *
 *   - EXACTLY ONE WITHDRAWAL PER TEND. Withdrawing does not move `lastTouchedAt` (see
 *     `withdrawTend`), so after the press the newest standing tend is the PREVIOUS one, whose `at`
 *     is earlier, and this returns `undefined`. A control that could be pressed repeatedly would
 *     walk backwards through the pile one stone at a time, and a cairn is "a pile that can only be
 *     added to" (design-direction §6 objection 1, "the design team's most important inheritance").
 *     One press, one correction.
 *   - IT IS SPENT BY CARRYING ON. Resume the loop, park it, write a note, tend it again — anything
 *     that touches it — and the undo is gone, because the mis-press is no longer the last thing
 *     that happened. That is what an undo means, and it needs no timer to mean it.
 *   - TIMERLESS AND RESTART-PROOF. `remove`'s undo (docs/DEVIATIONS.md D32) has no countdown and
 *     leaves when the user dismisses it; R160 asks for that grammar. Nothing here ticks, nothing is
 *     shown counting down, no prominence rises, and — unlike a component-state undo — this survives
 *     the app being closed, which matters because the harm it corrects lasts until the next window.
 *
 * A WINDOW BOUND WAS WEIGHED AND REFUSED. "Offer it while the tend is still suppressing a rhythm
 * window" reads well and leaves `tend` irreversible in exactly the cases R160 was written about:
 * `tend` accepts a Sunday chore tapped done on a Wednesday (it asks `isRepeating && isSurfaceable`,
 * not "are we in a window"), and that tend would have had no undo at all. An inverse with holes in
 * it is not an inverse.
 *
 * A LOOP THAT CANNOT COME ROUND ANSWERS `undefined`. `isSurfaceable` is asked for the reason `tend`
 * asks it (`RULINGS R125`): the two must not drift, and a loop that has ended or been settled is
 * not a loop the user is correcting a tend on — they have `rewarm` for that.
 *
 * A STONE DATED IN THE FUTURE IS NOT EVIDENCE, for `lastTendedAt`'s `asOf` reason: stones arrive
 * over sync from devices with wrong clocks and nothing clamps `at`.
 */
export function withdrawableTend(loop: Loop, now: number): SessionStone | undefined {
  if (!isSurfaceable(loop)) return undefined;

  let newest: SessionStone | undefined;
  for (const stone of standingStones(loop)) {
    if (stone.endedBy !== 'tended') continue;
    if (stone.at > now) continue; // An act cannot have happened yet. See `tendSatisfiesWindow`.
    if (newest === undefined || stone.at > newest.at) newest = stone;
  }
  if (!newest) return undefined;
  return newest.at === loop.lastTouchedAt ? newest : undefined;
}

/**
 * IS THIS LOOP'S RHYTHM RESTING? (`RULINGS R161`.)
 *
 * R161 ruled that settling a repeating loop SUSPENDS its rhythm condition rather than terminating
 * the loop: "rest suspends the rhythm and the loop returns to composting, recoverable by rewarm."
 * §8.3's whole point is that "deferral is legitimate", and the seasonal case is in the document's
 * own spirit — *don't ask me about the log store until autumn*. It adds NO fourth disposition:
 * the loop is settled, exactly as any other settled loop is, and `Temperature` still has five
 * members.
 *
 * IT IS A PREDICATE BECAUSE THE SURFACE HAS TO BE ABLE TO SAY THE TRUE THING. Before R161 the loop
 * detail surface rendered nothing at all about the rhythm of a settled repeating loop, which reads
 * as "this does not repeat" — the termination R161 says settling is not. What it should say is that
 * the rhythm is resting and a rewarm brings it back, and that needs a name to branch on.
 *
 * `isOpen` is part of it: a RELEASED chore's rhythm is not resting, it is over (R124 item 7 makes
 * stopping a rhythm `released`, one loop at a time, chosen).
 */
export function rhythmSuspended(loop: Loop): boolean {
  return isRepeating(loop) && isOpen(loop) && isSettled(loop);
}

/**
 * WHEN A REPEATING LOOP COMES ROUND, AS FACTS — never as a sentence (`RULINGS R162`).
 *
 * `surfacing.rhythm` renders as a bare axis label ("When it comes round" / "Repeating time
 * condition") and the condition's `weekday`, `hourFrom` and `hourTo` are shown to the user
 * NOWHERE — so the app can say a thing repeats and cannot say when. R162 authorises the formatter,
 * and product-concept §8.1's own example is "Sunday-morning loops on Sunday morning", so the
 * vocabulary is quoted rather than invented.
 *
 * CORE RETURNS FACTS AND `packages/copy` RENDERS THEM. `RULINGS R28`'s precedent, taken literally:
 * a sentence built here would freeze one register's English into a derivation, and `R27` records
 * what that costs. The weekday is a NUMBER, because a weekday name is data the platform's own
 * locale knows how to render (`formatDate` in `@carn/ui` makes the same argument); the part of day
 * is a `TimeOfDayBand`, which the catalogue already names.
 *
 * CAPPED AT `RULINGS R39`'s GRANULARITY — THE DAY AND A PART OF IT, NEVER AN INSTANT. R39 stopped
 * the app rendering "11:59 PM" because it "implies a precision the user did not state", and a
 * rhythm window is exactly that case again: Sorting PROPOSES these conditions, so the user often
 * did not author the hours at all. `hourFrom`/`hourTo` therefore resolve to a band or to nothing.
 * There is no path from here to a clock time.
 *
 * A BAND IS NAMED ONLY WHEN THE WHOLE WINDOW SITS INSIDE ONE, which is the honest rule rather than
 * the convenient one. `{hourFrom: 7, hourTo: 9}` is entirely inside `morning`, so "Sunday
 * mornings" is true. `{hourFrom: 7, hourTo: 14}` straddles morning and afternoon, and calling it
 * either would be the app stating something the condition does not say — so the band is omitted and
 * the caller renders the day alone. A condition with NEITHER hour is an all-day condition and gets
 * no band for the same reason, rather than being read as `night` because midnight happens to fall
 * in that band.
 *
 * NOTHING HERE IS A CLAIM ABOUT THE FUTURE. It describes the CONDITION — "Sunday mornings" — and
 * never a next occurrence. "Due Sunday" would make a missed Sunday into something that had been
 * missed, and no object in this model can be late (`tend` in ./events.ts).
 */
export type RhythmDescription = {
  /** `Date.getDay()`'s numbering: 0 = Sunday. Absent when the condition names no weekday. */
  readonly weekday?: number;
  /** Absent when the window straddles two bands, or names no hours at all. */
  readonly band?: TimeOfDayBand;
};

export function describeRhythm(condition: SurfacingCondition): RhythmDescription | undefined {
  if (condition.axis !== 'rhythm') return undefined;

  const { weekday, hourFrom, hourTo } = condition;
  const bounded = hourFrom !== undefined || hourTo !== undefined;
  let band: TimeOfDayBand | undefined;
  if (bounded) {
    // `hourTo` is EXCLUSIVE (`evaluateCondition`: `hour < hourTo`), so the last hour the window
    // actually contains is `hourTo - 1`. Testing `hourTo` itself would call a 09:00–12:00 window
    // "afternoon" on the strength of an hour it never matches.
    const first = bandForHour(hourFrom ?? 0);
    const last = bandForHour((hourTo ?? 24) - 1);
    if (first === last) band = first;
  }

  if (weekday === undefined && band === undefined) return undefined;
  return { ...(weekday !== undefined ? { weekday } : {}), ...(band !== undefined ? { band } : {}) };
}

/**
 * The loop's rhythm, described — the first condition that has anything to say.
 *
 * FIRST, NOT ALL, and the bound is deliberate. `RULINGS R124`'s bounded limitation records that two
 * rhythm conditions on one loop read as ONE chore rather than as "twice a day" — `lastTendedAt` is
 * per loop and cannot express "one of the two" — and no shipped path creates a second one (Sorting
 * proposes none; the fixture carries one). Rendering a list of two would present a vocabulary the
 * model does not have. If the interval vocabulary is ever authorised (R124 item 2), this is one of
 * the places it has to grow.
 */
export function loopRhythmDescription(loop: Loop): RhythmDescription | undefined {
  for (const condition of rhythmConditionsOf(loop)) {
    const described = describeRhythm(condition);
    if (described) return described;
  }
  return undefined;
}

/**
 * Accumulated recorded work. A fold over the stone log, not a stored total.
 *
 * `standingStones` for consistency with `sessionCount` (`RULINGS R160`), though a tend's
 * `durationMs` is 0 in the overwhelmingly common case and a withdrawal therefore usually changes
 * nothing here. It is NOT written as `loop.stones` on that basis: `tend` accepts an optional
 * session, so a timed tend can carry real minutes, and a number that disagreed with the cairn beside
 * it would be the drift `RULINGS R125` names.
 */
export function investedMs(loop: Loop): number {
  let total = 0;
  for (const stone of standingStones(loop)) total += Math.max(0, stone.durationMs);
  return total;
}

/** The capsule the "Where was I?" surface renders (product-concept §6.3). */
export function currentCapsule(loop: Loop): Capsule | undefined {
  return loop.capsules.length === 0 ? undefined : loop.capsules[loop.capsules.length - 1];
}

/**
 * The current ending, or `undefined` for an open loop. Derived from the log, never filed.
 *
 * It WALKS BACK rather than reading the last record, because two of the four record kinds
 * are not endings:
 *
 *   - `annotated` (RULINGS R30) attaches an insight to the ending beneath it. The newest
 *     annotation wins, so a user who keeps a second note sees the second note — and the
 *     ending's own `at` is untouched, which is the whole point: it used to become the moment
 *     the user finished reading.
 *   - `merged` (RULINGS R32) flattens to `closed` in `state`, because `TerminalTemperature` has
 *     exactly two members and never gains a third — but `kind` keeps saying `merged`, because
 *     the flattening is for DISPLAY ONLY (RULINGS R78) and no count may read `state`.
 *     `mergedIntoLoopId` is how a surface offers the survivor instead of pretending nothing
 *     happened.
 *
 * A `reopened` record still means "open": `rewarm` appends rather than deleting, so the
 * previous ending stays in the log for ever (product-concept §5, objection 3).
 *
 * WRITTEN AS AN EXHAUSTIVE `switch`, deliberately. Widening `TerminalRecord` from three kinds
 * to five produced no compiler error anywhere in the repository, because every consumer was a
 * chain of `if`s (the critique's S2.10). The `never` default below means a sixth record kind
 * cannot be added without this function refusing to compile — the same discipline
 * `TEMPERATURE_GLYPH`'s `satisfies` applies to the temperature vocabulary.
 *
 * An `annotated` record's insight is carried through onto WHATEVER ending it sits above,
 * including a `closed` one. The public API cannot build that lineage (`annotateEnding` refuses
 * a closed loop), but the record type permits it and a sync merge that reorders
 * `loop_terminals` rows can produce it, and silently discarding a note the user chose to keep
 * is the one outcome append-only history exists to prevent (the critique's S3.7).
 */
export function currentTerminal(loop: Loop): TerminalState | undefined {
  let insight: ReleaseObservation | undefined;
  for (let i = loop.terminals.length - 1; i >= 0; i -= 1) {
    const record = loop.terminals[i];
    if (!record) continue;
    switch (record.kind) {
      case 'annotated':
        insight ??= record.releaseInsight;
        continue;
      case 'reopened':
        return undefined;
      case 'closed':
        return {
          state: 'closed',
          kind: 'closed',
          at: record.at,
          quality: record.quality,
          releaseInsight: insight,
        };
      case 'merged':
        return {
          state: 'closed',
          kind: 'merged',
          at: record.at,
          mergedIntoLoopId: record.intoLoopId,
          releaseInsight: insight,
        };
      case 'released':
        return {
          state: 'released',
          kind: 'released',
          at: record.at,
          releaseInsight: insight ?? record.releaseInsight,
        };
      default: {
        const unreachable: never = record;
        return unreachable;
      }
    }
  }
  return undefined;
}

export function isOpen(loop: Loop): boolean {
  return currentTerminal(loop) === undefined;
}

/** True when the last settlement marker settled it (critique 2.5). */
export function isSettled(loop: Loop): boolean {
  const markers = loop.settlements ?? [];
  const last = markers.length === 0 ? undefined : markers[markers.length - 1];
  return last?.kind === 'settled';
}

export function settledAt(loop: Loop): number | undefined {
  const markers = loop.settlements ?? [];
  const last = markers.length === 0 ? undefined : markers[markers.length - 1];
  return last?.kind === 'settled' ? last.at : undefined;
}

/**
 * CAN THIS LOOP STILL BE BROUGHT ROUND BY A TRIGGER? Two conditions, one name (`RULINGS R125`).
 *
 * It exists because the two conditions had drifted apart between the two places that must agree
 * about them, and the drift was user-reachable. `evaluateSurfacing` skips `!isOpen(loop) ||
 * isSettled(loop)` — a terminal loop has ended, and a loop the user tucked into compost must not
 * be dragged back out by a geofence. `tend`'s guard checked `isOpen` ALONE, and `isOpen` reads
 * terminals only, so a SETTLED repeating loop accepted a tend: stone placed, `lastTouchedAt`
 * bumped, full celebration — and it would never surface again, because the settle marker still
 * excluded it. Reachable in five taps (accept the warm-cap trade on "feed the chickens", open it,
 * press the tend affordance), and it is precisely the zombie task §6.4 exists to kill, dressed as
 * a success. The tend also could not be undone, so it was the quiet history-editing `tend`'s own
 * docblock refuses.
 *
 * WHICH IS WHY IT IS A PREDICATE AND NOT TWO CALLS SIDE BY SIDE. `isMergedAway` and `isSettled`
 * are named for the same reason: the rule is a product rule, so one definition, and a third site
 * that needs it inherits both halves or neither. Any future transition whose contract is "the loop
 * stays available to come round" belongs here too.
 *
 * `isDiscarded` is deliberately NOT folded in: `apps/mobile`'s repository returns no discarded
 * loop from any read, so no caller can present one, and folding it in would imply that callers may
 * pass one.
 */
export function isSurfaceable(loop: Loop): boolean {
  return isOpen(loop) && !isSettled(loop);
}

// ---------------------------------------------------------------------------
// Temperature derivation — LAW 2
// ---------------------------------------------------------------------------

export type DerivationOptions = {
  readonly now: number;
  readonly warmCap: number;
  readonly windows?: TemperatureWindows;
  /**
   * "Which loop am I on right now" — ephemeral session state, owned by the app's store
   * (implementation-brief §5), not by the Loop. Singular by construction.
   */
  readonly activeLoopId?: string;
  /**
   * Storm mode (product-concept §5, objection 2): a chaotic day. Everything is
   * triage, judgment is suspended, cleanup deferred to the next Sorting session.
   * The cap does not apply.
   */
  readonly stormMode?: boolean;
};

/** The only place a `DerivedTemperature` is minted. Private to this module. */
function verdict(t: Temperature, reason: TemperatureReason): TemperatureVerdict {
  return { temperature: t as DerivedTemperature, reason };
}

/**
 * Derive every loop's temperature in one pass.
 *
 * Why a whole-set function rather than per-loop: `warm` is inherently relative — it
 * means "among the few most recently touched, up to the cap". A per-loop
 * `getTemperature(loop)` would be a lie, and lies in this function are how the warm
 * cap turns into a filing chore for the user.
 *
 * THE WARM SET HAS A FLOOR (critique 2.1). The top-ranked `min(cap, openCount)` loops
 * are warm *regardless of the recency window*; the window only decides
 * cooling-vs-composting for the remainder. Without this, a 30-day-lapsed user opened
 * Carn to "Nothing warm right now" — while holding forty live obligations — and
 * implementation-brief §11's "returns a 30-day-lapsed test user to a warm, wall-free
 * welcome" was unimplementable, with product-concept §11's "here is the one warm thing
 * that still matters" shipped as a string that could never be true.
 *
 * Consequence, accepted deliberately: a user with fewer open loops than their cap has
 * nothing composting. That is correct — three open loops is not a backlog, and the
 * explicit settle marker (§11's bulk compost) is how a small set reaches compost.
 *
 * Admitted above the cap, because the cap is soft:
 *   - the active loop, unconditionally (critique 2.3);
 *   - any loop whose `deadlineProminence >= DEADLINE_WARM_THRESHOLD` (critique 2.2) —
 *     design-direction §3 objection 3 bought the no-overdue law with "prominence
 *     including persistence", and a vet appointment sinking into the cooling list two
 *     days out is that debt going unpaid;
 *   - a loop the user has caught and never once picked up (`reason: 'caught'`, below).
 *
 * WHAT `reason: 'caught'` IS FOR (docs/DEVIATIONS.md D44, amending D38). The warm cap is
 * "how many things are on the go at once" (product-concept §5), and a thing nobody has
 * started is not on the go — so it does not spend a slot, and the loops the user is
 * actually in the middle of are not pushed off their own surface by this morning's
 * captures. It is a REASON and not a sixth `Temperature`: the vocabulary is closed at five
 * (CLAUDE.md), the loop is still warm, and it still cools on exactly the same schedule as
 * everything else — the clause below is inside the warm window, so nothing sits caught for
 * ever. This is the same shape `over-cap` already has: core distinguishes the fact, and the
 * copy and the surfaces branch on it so that what the app says is true.
 */
export function deriveTemperatures(
  loops: readonly Loop[],
  opts: DerivationOptions,
): Map<string, TemperatureVerdict> {
  const windows = opts.windows ?? DEFAULT_WINDOWS;
  const result = new Map<string, TemperatureVerdict>();

  const open: Loop[] = [];
  for (const loop of loops) {
    /**
     * A REMOVED LOOP HAS NO TEMPERATURE AT ALL (docs/DEVIATIONS.md D32).
     *
     * Not `closed`, not `composting` — no verdict, the same answer a list-bound loop gets
     * below, because "the quiet counts skip loops with no verdict" is what keeps a thing the
     * user took back out of the cooling count as well as off the cards. `apps/mobile`'s
     * repository already filters these out before any surface sees them; this is the second
     * gate, in the one function that assigns a temperature, so a caller holding a raw list
     * cannot accidentally file a removed loop into the warm set.
     */
    if (isDiscarded(loop)) continue;
    /**
     * A list-bound loop has NO temperature, and therefore never reaches the resumption
     * surface, the cooling count, the archive, or a compost review.
     *
     * Found by design review rather than by a test: with shopping items modelled as loops
     * carrying a `listId`, the seeded home surface showed "Sheep nuts, one bag" and
     * "Baler twine" as warm — four of the six visible cards were groceries, and they had
     * consumed the warm cap and pushed the half-finished fence job off the screen. That is
     * precisely the "beautiful forgetting machine" failure design-direction §4 objection 1
     * names, caused by milk.
     *
     * Authorised by product-concept §8.2, which gives shopping a DEDICATED mode rather
     * than making it "just another list": groceries are surfacing-driven (captured all
     * week, surfaced at the shop by geofence), not resumption-driven. Their home is their
     * list. Returning no verdict — rather than a `cooling` one — is what keeps them out of
     * the quiet counts too, since those skip loops with no verdict.
     *
     * IT IS ASKED BEFORE THE TERMINAL CHECK BELOW, AND THAT ORDER IS THE WHOLE RULE
     * (docs/DEVIATIONS.md `now-card/NC1`). Ticking a grocery off closes its loop
     * (`src/data/listProjection.ts` in apps/mobile: "a checked item is a CLOSED loop with its
     * history, not a deleted row"), so with the terminal check first, every item the user had
     * ever put in the trolley came back as `closed` — and `EverythingSurface` groups by
     * temperature, so the archive's Completed section filled up with milk, one row per shop.
     * The clause above says list-bound loops never reach "the cooling count, or a compost
     * review"; it was true of an OPEN grocery and false of a checked one, for no reason other
     * than which `if` ran first. A list item's home is its list in both states.
     *
     * Nothing is lost by it: the check mark on the list IS the item's completion, `isChecked`
     * in ./lists.ts reads the same append-only `terminals` log to draw it, and the loop's own
     * surface still states its ending in full. This gate decides one thing only — whether a
     * loop takes part in the temperature vocabulary — and a grocery never did.
     */
    if (loop.listId !== undefined) continue;
    const terminal = currentTerminal(loop);
    if (terminal) {
      result.set(loop.id, verdict(terminal.state, 'terminal'));
      continue;
    }
    open.push(loop);
  }

  const isActive = (l: Loop): boolean =>
    opts.activeLoopId !== undefined && l.id === opts.activeLoopId;
  const isDeadlineWarm = (l: Loop): boolean =>
    deadlineProminence(l.date, opts.now) >= DEADLINE_WARM_THRESHOLD;

  const warmCutoff = opts.now - windows.warmDays * DAY_MS;
  const coolingCutoff = opts.now - windows.coolingDays * DAY_MS;
  const cap = opts.stormMode ? Number.POSITIVE_INFINITY : clampWarmCap(opts.warmCap);

  // Active first, then imminent hard commitments, then explicitly-settled loops last
  // (they must not consume a warm slot), then most-recently-touched.
  const ranked = [...open].sort((a, b) => {
    const activeA = isActive(a);
    const activeB = isActive(b);
    if (activeA !== activeB) return activeA ? -1 : 1;
    const deadA = isDeadlineWarm(a);
    const deadB = isDeadlineWarm(b);
    if (deadA !== deadB) return deadA ? -1 : 1;
    if (deadA && deadB) {
      const pa = deadlineProminence(a.date, opts.now);
      const pb = deadlineProminence(b.date, opts.now);
      if (pa !== pb) return pb - pa;
    }
    const settledA = isSettled(a);
    const settledB = isSettled(b);
    if (settledA !== settledB) return settledA ? 1 : -1;
    return b.lastTouchedAt - a.lastTouchedAt;
  });

  let warmUsed = 0;
  for (const loop of ranked) {
    if (isActive(loop)) {
      result.set(loop.id, verdict('warm', 'active'));
      warmUsed += 1;
      continue;
    }
    if (isDeadlineWarm(loop)) {
      // Deliberately does NOT consume a warm slot: the cap is soft, and design-direction
      // §3 objection 3's promise of persistence must not be rationed by it.
      result.set(loop.id, verdict('warm', 'deadline'));
      continue;
    }
    if (isSettled(loop)) {
      // The user tucked this away. It floors to composting and is reversible by
      // `rewarm`, which appends an `unsettled` marker.
      result.set(loop.id, verdict('composting', 'settled'));
      continue;
    }
    /**
     * CAUGHT AND NEVER PICKED UP (docs/DEVIATIONS.md D44, amending D38).
     *
     * Warm — it is one of the most recent things the user cared about, which is what D38
     * settled and this does not disturb — but it spends no warm slot, because the cap
     * counts what is on the go and nothing has gone on here yet.
     *
     * INSIDE THE WARM WINDOW ONLY, and that is the clause that keeps the law. "Loops cool"
     * (CLAUDE.md): if this admitted a capture regardless of age, a note caught six months
     * ago would be warm for ever and could never reach compost — an exemption from time,
     * built out of inaction. Past the window it falls through to the ordinary
     * cooling/composting arithmetic below, with every other untouched loop.
     *
     * AND IT IS THE ONLY WARM ANSWER AN UNWORKED LOOP CAN GET — `worked` gates the two
     * clauses under it, which is not tidiness. Without that gate the rule inverts itself the
     * moment the warm set has room: a capture from this morning would be `caught` (a count in
     * the quiet row) while a capture from last week would take a free slot and be `recent` —
     * a full card on the resumption surface, offering to resume work that has never happened,
     * ranked above it. An unworked loop is `caught` while it is recent and cools with
     * everything else after that; it is never `recent` and never `over-cap`.
     */
    const worked = hasBeenWorked(loop);
    if (!worked && loop.lastTouchedAt >= warmCutoff) {
      result.set(loop.id, verdict('warm', 'caught'));
      continue;
    }
    if (worked && warmUsed < cap) {
      result.set(loop.id, verdict('warm', 'recent'));
      warmUsed += 1;
      continue;
    }
    if (worked && loop.lastTouchedAt >= warmCutoff) {
      // Touched minutes ago and over the cap. "Warm-adjacent but not shouting"
      // (product-concept §5) — the copy layer must NOT call this cold.
      result.set(loop.id, verdict('cooling', 'over-cap'));
      continue;
    }
    result.set(
      loop.id,
      loop.lastTouchedAt >= coolingCutoff
        ? verdict('cooling', 'window')
        : verdict('composting', 'window'),
    );
  }

  return result;
}

/** Convenience: the ids the home surface shows, in derivation order. */
export function warmSet(
  loops: readonly Loop[],
  opts: DerivationOptions,
): readonly Loop[] {
  const temps = deriveTemperatures(loops, opts);
  return orderWarmSet(
    loops.filter((l) => temps.get(l.id)?.temperature === 'warm'),
    opts.now,
    opts.activeLoopId,
  );
}

export function clampWarmCap(cap: number): number {
  if (!Number.isFinite(cap)) return DEFAULT_WARM_CAP;
  return Math.min(MAX_WARM_CAP, Math.max(MIN_WARM_CAP, Math.round(cap)));
}

/**
 * Would promoting this loop push the warm set past the cap?
 *
 * The answer is advisory. Callers must render it as the gentle trade prompt
 * ("want to let one of these cool off?") and MUST proceed if the user declines —
 * "never a block" (product-concept §5).
 *
 * `warmCount` is always the TRUE count (critique 1.7): during a storm it used to
 * report 0, so any UI rendering the prompt's count would have told the user "You have 0
 * warm already" while eight loops sat on their home surface.
 *
 * `coolestWarm` IS THE COOLEST WARM LOOP THE CAP IS ALLOWED TO SPEND, WHICH IS NOT THE SAME
 * THING AS THE COOLEST WARM LOOP (docs/RULINGS.md R102, product-concept §5).
 *
 * It used to be the least-recently-touched warm loop full stop, and that nominated a hard
 * commitment: `isDeadlineWarm` admits a dated loop to the warm set regardless of when it was
 * last touched, so a vet appointment captured four days ago outranks everything on
 * `lastTouchedAt` and the rendered prompt read "Set Bramble's booster at the vet to idle".
 * Two defects in one nomination:
 *
 *   1. §5 gives a hard external deadline "calendar-grade treatment", and design-direction §3
 *      objection 3 bought the no-red law with PERSISTENCE. The warm cap's trade is about the
 *      USER's attention, not about what the world requires — so `bulkCompostOffer` already
 *      excludes this case, in exactly these words: "anything with a hard date still ahead of
 *      it: the world cares about that one, and **the app does not get to file it**."
 *   2. Accepting would have moved nothing. `deriveTemperatures` tests `isDeadlineWarm` BEFORE
 *      `isSettled`, so a settled deadline-warm loop stays warm: the marker would be written
 *      and the surface would not change. A prompt that offers an action which silently does
 *      nothing is worse than no prompt.
 *
 * So the exclusions are `bulkCompostOffer`'s, read off this module's own
 * `TemperatureVerdict.reason` rather than re-derived. `active` and `deadline` are the two
 * reasons a loop is warm for something other than the user's own recent attention, and
 * neither is the cap's to spend. `undefined` is the honest answer when no candidate survives:
 * `overCap` and `warmCount` still report the truth, and a caller with no candidate has
 * nothing to offer.
 *
 * A REPEATING LOOP IS THE THIRD EXCLUSION, AND IT IS R102's RULE APPLIED TO A WORSE CASE
 * (product-concept §5 and §8.1; the repeating-work design is `RULINGS R124`). A daily chore
 * tended every morning has `lastTouchedAt` permanently inside the cooling window, so it is
 * permanently warm — and warm STRUCTURALLY, because it comes round, not because the user chose to
 * hold it in mind. It is therefore a plausible `coolestWarm` nomination on any quiet day, and
 * accepting the trade settles it, and `evaluateSurfacing` excludes settled loops: **the chore
 * never comes round again.** The user believes they deferred something and has silently destroyed
 * it, with no indication anywhere and nothing to undo. R102's own words were "a prompt that offers
 * an action which silently does nothing is worse than no prompt" — this prompt does not do
 * nothing, it does harm, so the case for excluding is strictly stronger than the one that ruling
 * was written for. The rhythm is exactly the kind of warmth §5's trade is not about: the world
 * does not care when the chickens are fed, but the RHYTHM does, and the app does not get to file a
 * standing commitment any more than it gets to file a vet appointment.
 *
 * `isRepeating` counts a PROPOSED rhythm as well as a confirmed one, which is right here for the
 * reason it is right in `tend`: a loop the app itself will bring round on Sunday must not be
 * offered up on Saturday to a prompt that would stop it coming round.
 *
 * WHAT THIS DOES NOT DECIDE: whether a repeating loop should be settleable AT ALL, and by what
 * gesture. That is a product question, raised rather than answered — R124 item 7 says stopping is
 * `released`, which suggests the answer, but `settle` is reachable from other surfaces and this
 * function has no business ruling on them. All that is settled here is that the warm cap's trade
 * does not nominate one.
 */
export function warmCapTrade(
  loops: readonly Loop[],
  opts: DerivationOptions,
): { overCap: boolean; warmCount: number; coolestWarm?: Loop } {
  const temps = deriveTemperatures(loops, opts);
  const warm = loops.filter((l) => temps.get(l.id)?.temperature === 'warm');
  const cap = clampWarmCap(opts.warmCap);
  const coolestWarm = warm
    .filter((l) => {
      const reason = temps.get(l.id)?.reason;
      if (reason === 'active' || reason === 'deadline') return false;
      // Warm because it comes round, not because the user is holding it. Cooling it off would
      // stop it coming round at all.
      return !isRepeating(l);
    })
    .reduce<Loop | undefined>(
      (coolest, loop) =>
        coolest === undefined || loop.lastTouchedAt < coolest.lastTouchedAt ? loop : coolest,
      undefined,
    );
  return {
    // The cap is suspended in a storm; the count is still the truth.
    overCap: opts.stormMode ? false : warm.length >= cap,
    warmCount: warm.length,
    coolestWarm,
  };
}

// ---------------------------------------------------------------------------
// Ordering for the resumption surface
// ---------------------------------------------------------------------------

/**
 * How much there is to RECOGNISE in a loop's current capsule: 0..3.
 *
 * Authorised by product-concept §6.3, which is a claim about time — "re-entry cost drops
 * from minutes of reconstruction to seconds of recognition" — and which names the three
 * things the surface shows: "the photo of the half-done fence, the voice line, 'next: get
 * the 13mm from the jacket'". This function counts how many of those a loop actually has.
 *
 * WHY IT EXISTS (critique 1.2). Ordering was active, then deadline, then `lastTouchedAt`
 * descending. Against the shipped fixture that put the two EMPTIEST capsules — both
 * rendering "No bookmark on this one yet" — ahead of the Tier-2 loop with the photo and the
 * voice line, which landed FIFTH, roughly two and a half screenfuls down. Recency is the
 * wrong sole tie-break for a surface whose job is recognition: a loop touched five hours ago
 * with nothing in its capsule offers nothing to recognise, and one touched twenty-five hours
 * ago with a photo and a voice line offers everything.
 *
 * IT IS NOT "SMART REARRANGEMENT" (design-direction §4, which forbids that by name). It is
 * a fixed, declared, inspectable fold over data the user themselves put there. Nothing here
 * learns, personalises, A/B-tests, or changes between two runs on the same data — the
 * property §4 and product-concept §11's interface-stability promise actually protect.
 *
 * `lastStep` deliberately scores nothing on its own: "where you got to" is context, not a
 * handhold, and a loop with only a last step still reads as a bare card.
 */
export function recognisability(loop: Loop): number {
  const capsule = currentCapsule(loop);
  if (!capsule) return 0;
  let score = 0;
  if (capsule.nextAction) score += 1;
  if (capsule.photoUri) score += 1;
  if (capsule.voiceNoteUri || capsule.voiceNoteTranscript) score += 1;
  return score;
}

/**
 * Ordering for the "Where was I?" surface (product-concept §6.3).
 *
 * Deliberately NOT "smart": the spatial grammar is fixed and the home surface is never
 * rearranged by an algorithm (design-direction §4 — "no 'smart' rearrangement of the
 * home surface"). Every lift here is a declared rule over the user's own data, in this
 * order and no other:
 *
 *   1. the loop you are on right now;
 *   2. hard-deadline prominence — prominence by position, exactly as §3 objection 3
 *      requires, and it decays after the date passes so it cannot pin anything for ever;
 *   3. recognisability — how much of §6.3's photo / voice line / next action is present;
 *   4. recency.
 *
 * 3 sits above 4 because §6.3 specifies an OUTCOME in seconds, not a sort key.
 */
export function orderWarmSet(
  warm: readonly Loop[],
  now: number,
  activeLoopId?: string,
): readonly Loop[] {
  return [...warm].sort((a, b) => {
    const activeA = a.id === activeLoopId;
    const activeB = b.id === activeLoopId;
    if (activeA !== activeB) return activeA ? -1 : 1;
    const pa = deadlineProminence(a.date, now);
    const pb = deadlineProminence(b.date, now);
    // Only a genuinely imminent hard commitment reorders anything.
    if (pa >= DEADLINE_WARM_THRESHOLD || pb >= DEADLINE_WARM_THRESHOLD) {
      if (pa !== pb) return pb - pa;
    }
    const ra = recognisability(a);
    const rb = recognisability(b);
    if (ra !== rb) return rb - ra;
    return b.lastTouchedAt - a.lastTouchedAt;
  });
}
